import React, {useRef, useState, useEffect, useCallback, createContext, useContext} from 'react';
import {createReactBlockSpec} from '@blocknote/react';
import {fetchBrands, fetchBrandProducts, createCheckoutCart} from '../../utils/brandsApi';

// ============================================
// GLOBAL INPUT FOCUS STYLES
// ============================================
// Inject global styles for input focus states (cursor visibility, border highlight)
const inputFocusStyleId = 'custom-blocks-input-focus-styles';
if (typeof document !== 'undefined' && !document.getElementById(inputFocusStyleId)) {
    const styleSheet = document.createElement('style');
    styleSheet.id = inputFocusStyleId;
    styleSheet.textContent = `
        .settings-input:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15) !important;
            caret-color: #000 !important;
        }
        .settings-input::placeholder {
            color: #999;
        }
        .settings-textarea:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15) !important;
            caret-color: #000 !important;
        }
    `;
    document.head.appendChild(styleSheet);
}

// ============================================
// GLOBAL CART CONTEXT (for brand products across all blocks)
// ============================================
const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    // Return empty cart functions if not in provider (editor mode)
    if (!context) {
        return {
            cart: [],
            cartCount: 0,
            cartTotal: 0,
            profileId: null,
            addToCart: () => {},
            removeFromCart: () => {},
            updateQuantity: () => {},
            isInCart: () => false,
            clearCart: () => {}
        };
    }
    return context;
};

/** @param {{ children: React.ReactNode, profileId?: number | null }} props - profileId = designer profile (jis page se customer shop kar raha hai) */
export const CartProvider = ({ children, profileId = null }) => {
    const [cart, setCart] = useState([]);
    const [showCheckout, setShowCheckout] = useState(false);

    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const cartTotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * item.quantity, 0);

    const addToCart = useCallback((item) => {
        setCart(prev => {
            const exists = prev.find(c => c.id === item.id || c.productId === item.productId);
            if (exists) {
                return prev.map(c =>
                    (c.id === item.id || c.productId === item.productId)
                        ? {...c, quantity: c.quantity + 1}
                        : c
                );
            }
            return [...prev, {...item, quantity: 1}];
        });
    }, []);

    const removeFromCart = useCallback((itemId) => {
        setCart(prev => prev.filter(c => c.id !== itemId && c.productId !== itemId));
    }, []);

    const updateQuantity = useCallback((itemId, quantity) => {
        if (quantity <= 0) {
            removeFromCart(itemId);
        } else {
            setCart(prev => prev.map(c =>
                (c.id === itemId || c.productId === itemId)
                    ? {...c, quantity}
                    : c
            ));
        }
    }, [removeFromCart]);

    const isInCart = useCallback((itemId, productId) => {
        return cart.some(c => c.id === itemId || (productId && c.productId === productId));
    }, [cart]);

    const clearCart = useCallback(() => {
        setCart([]);
    }, []);

    return (
        <CartContext.Provider value={{
            cart,
            cartCount,
            cartTotal,
            profileId: profileId ?? null,
            addToCart,
            removeFromCart,
            updateQuantity,
            isInCart,
            clearCart,
            showCheckout,
            setShowCheckout
        }}>
            {children}
        </CartContext.Provider>
    );
};

// ============================================
// FLOATING CHECKOUT BAR COMPONENT
// ============================================
export const FloatingCheckoutBar = () => {
    const {cart, cartCount, cartTotal, profileId, removeFromCart, updateQuantity, showCheckout, setShowCheckout, clearCart} = useCart();
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);

    const handleProceedToCheckout = useCallback(async () => {
        const shopifyItems = cart.filter(item => item.productId).map(item => ({
            productId: item.productId,
            quantity: item.quantity || 1
        }));
        if (shopifyItems.length === 0) {
            setCheckoutError('Cart has no Shopify products to checkout.');
            return;
        }
        setCheckoutError(null);
        setCheckoutLoading(true);
        try {
            const result = await createCheckoutCart({ items: shopifyItems, profileId: profileId ?? undefined });
            if (result.ok && result.data?.checkoutUrl) {
                window.location.href = result.data.checkoutUrl;
                return;
            }
            setCheckoutError(result.error || 'Checkout failed.');
        } catch (e) {
            setCheckoutError(e?.message || 'Checkout failed.');
        } finally {
            setCheckoutLoading(false);
        }
    }, [cart, profileId]);

    if (cartCount === 0) return null;

    return (
        <>
            {/* Floating Cart Bar */}
            <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                borderTop: '1px solid #e5e5e5',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                zIndex: 1000
            }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <div style={{
                        position: 'relative',
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                            <circle cx="9" cy="21" r="1"/>
                            <circle cx="20" cy="21" r="1"/>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                        </svg>
                        <span style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            backgroundColor: '#ef4444',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 600,
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {cartCount}
                        </span>
                    </div>
                    <div>
                        <div style={{fontSize: '14px', fontWeight: 600, color: '#1a1a1a'}}>
                            {cartCount} {cartCount === 1 ? 'Item' : 'Items'}
                        </div>
                        <div style={{fontSize: '16px', fontWeight: 700, color: '#059669'}}>
                            ${cartTotal.toFixed(2)}
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => setShowCheckout(true)}
                    style={{
                        backgroundColor: '#1a1a1a',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px 28px',
                        fontSize: '15px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    Checkout
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                </button>
            </div>

            {/* Checkout Modal */}
            {showCheckout && (
                <>
                    <div
                        onClick={() => setShowCheckout(false)}
                        style={{
                            position: 'fixed',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1001
                        }}
                    />
                    <div style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        borderTopLeftRadius: '20px',
                        borderTopRightRadius: '20px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 1002
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 20px',
                            borderBottom: '1px solid #e5e5e5'
                        }}>
                            <h3 style={{margin: 0, fontSize: '18px', fontWeight: 600}}>Your Cart</h3>
                            <button
                                onClick={() => setShowCheckout(false)}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Cart Items */}
                        <div style={{flex: 1, overflow: 'auto', padding: '16px 20px'}}>
                            {cart.map(item => (
                                <div key={item.id || item.productId} style={{
                                    display: 'flex',
                                    gap: '12px',
                                    padding: '12px 0',
                                    borderBottom: '1px solid #f0f0f0'
                                }}>
                                    <div style={{
                                        width: '70px',
                                        height: '70px',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        backgroundColor: '#f5f5f5',
                                        flexShrink: 0
                                    }}>
                                        {item.imageUrl ? (
                                            <img src={item.imageUrl} alt={item.name || item.productName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                        ) : (
                                            <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '11px'}}>
                                                No image
                                            </div>
                                        )}
                                    </div>
                                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                        <div style={{fontWeight: 600, fontSize: '14px', color: '#1a1a1a', marginBottom: '4px'}}>
                                            {item.name || item.productName}
                                        </div>
                                        <div style={{fontSize: '15px', fontWeight: 700, color: '#059669'}}>
                                            ${parseFloat(item.price).toFixed(2)}
                                        </div>
                                        {item.brandName && (
                                            <div style={{fontSize: '12px', color: '#3b82f6', marginTop: '2px'}}>{item.brandName}</div>
                                        )}
                                    </div>
                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: '8px'}}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '8px',
                                            padding: '4px'
                                        }}>
                                            <button
                                                onClick={() => updateQuantity(item.id || item.productId, item.quantity - 1)}
                                                style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#fff',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    color: '#666'
                                                }}
                                            >
                                                −
                                            </button>
                                            <span style={{width: '24px', textAlign: 'center', fontWeight: 600}}>{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.id || item.productId, item.quantity + 1)}
                                                style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#fff',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    color: '#666'
                                                }}
                                            >
                                                +
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => removeFromCart(item.id || item.productId)}
                                            style={{
                                                border: 'none',
                                                backgroundColor: 'transparent',
                                                color: '#ef4444',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                padding: '4px 8px'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '16px 20px',
                            borderTop: '1px solid #e5e5e5',
                            backgroundColor: '#fafafa'
                        }}>
                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '16px'}}>
                                <span style={{fontSize: '16px', fontWeight: 600}}>Total</span>
                                <span style={{fontSize: '20px', fontWeight: 700, color: '#059669'}}>${cartTotal.toFixed(2)}</span>
                            </div>
                            {checkoutError && (
                                <div style={{marginBottom: '12px', padding: '10px', backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: '8px', fontSize: '14px'}}>
                                    {checkoutError}
                                </div>
                            )}
                            <button
                                onClick={handleProceedToCheckout}
                                disabled={checkoutLoading}
                                style={{
                                    width: '100%',
                                    backgroundColor: checkoutLoading ? '#9ca3af' : '#1a1a1a',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    cursor: checkoutLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {checkoutLoading ? 'Creating checkout…' : 'Proceed to Checkout'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

// ============================================
// BLOCK TOOLBAR COMPONENT (Simplified - toolbar moved to SideMenu)
// ============================================
// The 3-dots toolbar has been moved to the grid/drag handle menu (SideMenu)
// This component now just wraps children without the toolbar UI
const BlockToolbar = ({block, editor, children, blockOptions = [], blockActions = []}) => {
    return (
        <div style={{position: 'relative', width: '100%'}}>
            {children}
        </div>
    );
};

// Legacy BlockToolbar styles kept for reference but no longer used
const _legacyBlockToolbarNote = `
// blockOptions format: [{label, prop, options: [{value, label, icon?}]}]
// blockActions format: [{label, icon?, onClick}] - for custom action buttons
// All toolbar options are now in BlockNoteDesigner.jsx CustomDragHandleMenuContent
`;

// Legacy toolbarStyles kept for any components that may still reference it
const toolbarStyles = {
    hoverBar: {
        position: 'absolute',
        top: '16px',
        right: '8px',
        display: 'flex',
        gap: '4px',
        zIndex: 10
    },
    hoverButton: {
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    backdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99
    },
    menu: {
        position: 'absolute',
        top: '40px',
        right: '8px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        minWidth: '200px',
        zIndex: 100,
        padding: '8px 0',
        border: '1px solid #e0e0e0'
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#333',
        position: 'relative'
    },
    menuItemLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    menuItemRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    divider: {
        height: '1px',
        backgroundColor: '#e0e0e0',
        margin: '8px 0'
    },
    submenu: {
        position: 'absolute',
        left: '100%',
        top: '0',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        minWidth: '180px',
        padding: '8px 0',
        border: '1px solid #e0e0e0',
        marginLeft: '4px'
    },
    submenuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#333'
    },
    colorSection: {
        padding: '12px 16px'
    },
    colorLabel: {
        fontSize: '12px',
        color: '#666',
        marginBottom: '8px'
    },
    colorGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    colorSwatch: {
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'transform 0.1s'
    }
};

// ============================================
// ASK AI BLOCK
// ============================================
export const AskAIBlock = createReactBlockSpec(
    {
        type: 'askAI',
        propSchema: {
            aiName: {default: 'AI'},
            questionPlaceholder: {default: "What's my camera setup?"},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;

            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Alignment',
                            prop: 'alignment',
                            options: [
                                {value: 'left', label: 'Left'},
                                {value: 'center', label: 'Center'},
                                {value: 'right', label: 'Right'}
                            ]
                        }
                    ]}
                >
                    <div style={{
                        ...askAIStyles.container,
                        backgroundColor: block.props.backgroundColor || '#fff',
                        textAlign: block.props.alignment || 'center'
                    }}>
                        <div style={{...askAIStyles.labelRow, color: block.props.textColor || '#1a1a1a'}}>
                            <span>Ask </span>
                            <input
                                type="text"
                                value={block.props.aiName}
                                onChange={(e) => {
                                    editor.updateBlock(block, {
                                        props: {...block.props, aiName: e.target.value}
                                    });
                                }}
                                style={askAIStyles.nameInput}
                                placeholder="AI Name"
                            />
                            <span>:</span>
                        </div>
                        <div style={askAIStyles.inputRow}>
                            <input
                                type="text"
                                value={block.props.questionPlaceholder}
                                onChange={(e) => {
                                    editor.updateBlock(block, {
                                        props: {...block.props, questionPlaceholder: e.target.value}
                                    });
                                }}
                                style={askAIStyles.input}
                                placeholder="Enter placeholder question..."
                            />
                            <button style={askAIStyles.askButton}>
                                Ask
                            </button>
                        </div>
                    </div>
                </BlockToolbar>
            );
        }
    }
);

const askAIStyles = {
    container: {
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        padding: '16px',
        margin: '8px 0'
    },
    labelRow: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a',
        marginBottom: '8px',
        gap: '4px'
    },
    nameInput: {
        border: 'none',
        borderBottom: '1px dashed #ccc',
        fontSize: '14px',
        fontWeight: 500,
        outline: 'none',
        backgroundColor: 'transparent',
        width: '80px',
        textAlign: 'center'
    },
    inputRow: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
    },
    input: {
        flex: 1,
        padding: '10px 14px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        backgroundColor: '#fafafa'
    },
    askButton: {
        padding: '10px 20px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer'
    }
};

// ============================================
// SHOPPABLE POST BLOCK
// ============================================
export const ShoppablePostBlock = createReactBlockSpec(
    {
        type: 'shoppablePost',
        propSchema: {
            title: {
                default: 'Shoppable Post'
            },
            description: {
                default: 'Add a description for your post'
            },
            imageUrl: {
                default: ''
            },
            size: {
                default: 'full',
                values: ['small', 'full']
            },
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const fileInputRef = useRef(null);
            const size = block.props.size || 'full';
            const isSmall = size === 'small';

            const handleImageUpload = (e) => {
                const file = e.target.files?.[0];
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        editor.updateBlock(block, {
                            props: {...block.props, imageUrl: reader.result}
                        });
                    };
                    reader.readAsDataURL(file);
                }
            };

            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Size',
                            prop: 'size',
                            options: [
                                {value: 'full', label: 'Full Width'},
                                {value: 'small', label: 'Half Width'}
                            ]
                        }
                    ]}
                >
                    <div style={{
                        ...shoppableStyles.container,
                        width: isSmall ? '48%' : '100%',
                        display: isSmall ? 'inline-block' : 'block',
                        backgroundColor: block.props.backgroundColor || '#fff',
                        textAlign: block.props.alignment || 'center'
                    }}>
                    <div style={shoppableStyles.header}>
                        <input
                            type="text"
                            value={block.props.title}
                            onChange={(e) => {
                                editor.updateBlock(block, {
                                    props: {...block.props, title: e.target.value}
                                });
                            }}
                            style={shoppableStyles.titleInput}
                            placeholder="Post Title"
                        />
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{display: 'none'}}
                    />

                    {block.props.imageUrl ? (
                        <div style={shoppableStyles.imageContainer}>
                            <img src={block.props.imageUrl} alt="" style={{...shoppableStyles.image, maxHeight: isSmall ? '150px' : 'none'}} />
                            <button
                                onClick={() => editor.updateBlock(block, {props: {...block.props, imageUrl: ''}})}
                                style={shoppableStyles.removeBtn}
                            >
                                ×
                            </button>
                        </div>
                    ) : (
                        <div
                            style={{...shoppableStyles.uploadArea, padding: isSmall ? '20px 10px' : '40px 20px'}}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21,15 16,10 5,21"/>
                            </svg>
                            <span style={{fontSize: isSmall ? '12px' : '14px'}}>Click to upload</span>
                        </div>
                    )}

                    <textarea
                        value={block.props.description}
                        onChange={(e) => {
                            editor.updateBlock(block, {
                                props: {...block.props, description: e.target.value}
                            });
                        }}
                        style={{...shoppableStyles.description, fontSize: isSmall ? '12px' : '14px'}}
                        placeholder="Add a description..."
                        rows={isSmall ? 1 : 2}
                    />
                    </div>
                </BlockToolbar>
            );
        }
    }
);

const shoppableStyles = {
    container: {
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '8px 4px 8px 0',
        boxSizing: 'border-box',
        verticalAlign: 'top'
    },
    header: {
        padding: '10px 12px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
    },
    titleInput: {
        flex: 1,
        border: 'none',
        fontSize: '14px',
        fontWeight: 600,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        minWidth: 0
    },
    sizeToggle: {
        display: 'flex',
        gap: '2px',
        flexShrink: 0
    },
    sizeBtn: {
        width: '22px',
        height: '22px',
        border: '1px solid #e5e5e5',
        borderRadius: '4px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '11px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
    },
    sizeBtnActive: {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderColor: '#1a1a1a'
    },
    imageContainer: {
        position: 'relative',
        width: '100%'
    },
    image: {
        width: '100%',
        height: 'auto',
        display: 'block',
        objectFit: 'cover'
    },
    removeBtn: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.6)',
        color: '#fff',
        border: 'none',
        fontSize: '18px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    uploadArea: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#fafafa',
        cursor: 'pointer',
        color: '#999'
    },
    description: {
        width: '100%',
        padding: '10px 12px',
        border: 'none',
        color: '#666',
        outline: 'none',
        resize: 'none',
        boxSizing: 'border-box'
    }
};

// ============================================
// PRODUCT CARD BLOCK
// ============================================
export const ProductCardBlock = createReactBlockSpec(
    {
        type: 'productCard',
        propSchema: {
            productName: {default: 'Product Name'},
            price: {default: '0.00'},
            description: {default: ''},
            imageUrl: {default: ''},
            shopUrl: {default: ''},
            affiliateUrl: {default: ''},
            isAffiliate: {default: false},
            productId: {default: ''},
            brandName: {default: ''},
            buttonText: {default: 'Shop Now'},
            layout: {default: 'vertical', values: ['vertical', 'horizontal']},
            size: {default: 'full', values: ['small', 'full']},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'},
            _triggerBrowse: {default: null}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const fileInputRef = useRef(null);
            const isEditable = editor.isEditable;
            const layout = block.props.layout || 'vertical';
            const size = block.props.size || 'full';
            const isHorizontal = layout === 'horizontal';
            const isSmall = size === 'small';

            // Global cart for preview mode
            const {addToCart, isInCart} = useCart();
            const isBrandProduct = !!block.props.productId;
            const productInCart = isInCart(block.props.productId, block.props.productId);

            // Settings popup state
            const [showSettings, setShowSettings] = useState(false);

            // Draggable panel state
            const [panelPosition, setPanelPosition] = useState({x: 0, y: 0});
            const [isDragging, setIsDragging] = useState(false);
            const dragOffsetRef = useRef({x: 0, y: 0});

            const handleDragStart = (e) => {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
                setIsDragging(true);
                const rect = e.currentTarget.parentElement.getBoundingClientRect();
                dragOffsetRef.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };
                e.preventDefault();
            };

            // Document-level event listeners for smooth dragging
            useEffect(() => {
                if (!isDragging) return;

                const handleMouseMove = (e) => {
                    const newX = e.clientX - dragOffsetRef.current.x - window.innerWidth / 2 + 170;
                    const newY = e.clientY - dragOffsetRef.current.y - window.innerHeight / 2;
                    setPanelPosition({x: newX, y: newY});
                };

                const handleMouseUp = () => {
                    setIsDragging(false);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);

                return () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                };
            }, [isDragging]);

            const openSettings = () => {
                setPanelPosition({x: 0, y: 0});
                setShowSettings(true);
            };

            // Product browser state
            const [showBrowser, setShowBrowser] = useState(false);
            const [browserStep, setBrowserStep] = useState('brands');
            const [brands, setBrands] = useState([]);
            const [selectedBrand, setSelectedBrand] = useState(null);
            const [brandProducts, setBrandProducts] = useState([]);
            const [browserLoading, setBrowserLoading] = useState(false);
            const [browserSearch, setBrowserSearch] = useState('');

            // Load brands
            const loadBrands = useCallback(async () => {
                setBrowserLoading(true);
                const result = await fetchBrands({limit: 50});
                if (result.ok && result.data) {
                    setBrands(result.data.brands || []);
                }
                setBrowserLoading(false);
            }, []);

            // Load products for selected brand
            const loadBrandProducts = useCallback(async (brandSlug) => {
                setBrowserLoading(true);
                const result = await fetchBrandProducts({slug: brandSlug, limit: 50});
                if (result.ok && result.data) {
                    setBrandProducts(result.data.products || []);
                }
                setBrowserLoading(false);
            }, []);

            // Open browser
            const openBrowser = () => {
                setShowBrowser(true);
                setBrowserStep('brands');
                setSelectedBrand(null);
                setBrandProducts([]);
                setBrowserSearch('');
                loadBrands();
            };

            // Watch for _triggerBrowse prop change from side menu
            useEffect(() => {
                if (block.props._triggerBrowse) {
                    openBrowser();
                    // Clear the trigger prop
                    editor.updateBlock(block, {
                        props: {...block.props, _triggerBrowse: null}
                    });
                }
            }, [block.props._triggerBrowse]);

            // Select brand
            const selectBrand = (brand) => {
                setSelectedBrand(brand);
                setBrowserStep('products');
                loadBrandProducts(brand.slug);
            };

            // Select product from browser
            const selectProduct = (product) => {
                editor.updateBlock(block, {
                    props: {
                        ...block.props,
                        productName: product.title || 'Product',
                        price: product.retailPrice ? parseFloat(product.retailPrice).toFixed(2) : '0.00',
                        description: product.description || '',
                        imageUrl: product.imageUrl || '',
                        shopUrl: product.url || '',
                        affiliateUrl: product.affiliateUrl || '',
                        isAffiliate: !!product.affiliateUrl,
                        productId: product.id,
                        brandName: selectedBrand?.name || ''
                    }
                });
                setShowBrowser(false);
            };

            // Filter
            const filteredBrands = brands.filter(b => b.name?.toLowerCase().includes(browserSearch.toLowerCase()));
            const filteredProducts = brandProducts.filter(p => p.title?.toLowerCase().includes(browserSearch.toLowerCase()));

            // Image upload
            const handleImageUpload = (e) => {
                const file = e.target.files?.[0];
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        editor.updateBlock(block, {
                            props: {...block.props, imageUrl: reader.result}
                        });
                    };
                    reader.readAsDataURL(file);
                }
            };

            // Format price
            const formatPrice = (val) => {
                const num = parseFloat(val?.replace(/[^0-9.]/g, '')) || 0;
                return num.toFixed(2);
            };

            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {label: 'Layout', prop: 'layout', options: [{value: 'vertical', label: 'Vertical'}, {value: 'horizontal', label: 'Horizontal'}]},
                        {label: 'Size', prop: 'size', options: [{value: 'full', label: 'Full Width'}, {value: 'small', label: 'Half Width'}]}
                    ]}
                    blockActions={[
                        {label: 'Browse from Brands', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>', color: '#3b82f6', onClick: openBrowser}
                    ]}
                >
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{display: 'none'}} />

                    <div style={{
                        ...productStyles.container,
                        flexDirection: isHorizontal ? 'row' : 'column',
                        width: isSmall ? '48%' : '100%',
                        display: isSmall ? 'inline-flex' : 'flex'
                    }}>
                        {/* Image */}
                        <div
                            style={{
                                ...productStyles.imageWrapper,
                                width: isHorizontal ? '120px' : '100%',
                                height: isHorizontal ? '120px' : '180px',
                                cursor: isEditable ? 'pointer' : 'default'
                            }}
                            onClick={() => isEditable && fileInputRef.current?.click()}
                        >
                            {block.props.imageUrl ? (
                                <img src={block.props.imageUrl} alt={block.props.productName} style={productStyles.image} />
                            ) : (
                                <div style={productStyles.uploadArea}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                                        <circle cx="8.5" cy="8.5" r="1.5"/>
                                        <polyline points="21,15 16,10 5,21"/>
                                    </svg>
                                    {isEditable && <span style={{fontSize: '11px', color: '#999', marginTop: '4px'}}>Click to upload</span>}
                                </div>
                            )}
                        </div>

                        {/* Info */}
                        <div style={{...productStyles.info, padding: isHorizontal ? '0 0 0 12px' : '12px'}}>
                            {isBrandProduct && (
                                <div style={productStyles.brandTag}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5z"/></svg>
                                    {block.props.brandName || 'Brand'}
                                </div>
                            )}
                            <div style={productStyles.productName}>{block.props.productName || 'Product Name'}</div>
                            <div style={productStyles.productPrice}>${block.props.price || '0.00'}</div>
                            {block.props.description && (
                                <div style={productStyles.productDesc}>{block.props.description}</div>
                            )}

                            {/* Button */}
                            {isEditable ? (
                                <button style={productStyles.settingsBtn} onClick={openSettings}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="3"/>
                                        <path d="M12 1v6m0 10v6m-9-9h6m10 0h6"/>
                                    </svg>
                                    Edit Product
                                </button>
                            ) : isBrandProduct ? (
                                productInCart ? (
                                    <button style={{...productStyles.shopButton, backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'}}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                        </svg>
                                        In Cart
                                    </button>
                                ) : (
                                    <button
                                        style={productStyles.shopButton}
                                        onClick={() => addToCart({
                                            id: block.id,
                                            productId: block.props.productId,
                                            name: block.props.productName,
                                            productName: block.props.productName,
                                            price: block.props.price,
                                            imageUrl: block.props.imageUrl,
                                            brandName: block.props.brandName
                                        })}
                                    >
                                        Add to Cart
                                    </button>
                                )
                            ) : (
                                <a
                                    href={block.props.isAffiliate ? block.props.affiliateUrl : block.props.shopUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{...productStyles.shopButton, textDecoration: 'none', display: 'inline-block', textAlign: 'center'}}
                                >
                                    {block.props.buttonText || 'Shop Now'}
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Settings Popup */}
                    {showSettings && (
                        <>
                            <div
                                onClick={() => !isDragging && setShowSettings(false)}
                                style={{
                                    position: 'fixed',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                    zIndex: 999,
                                    cursor: isDragging ? 'grabbing' : 'default'
                                }}
                            />
                            <div style={{
                                ...productStyles.settingsPanel,
                                transform: `translate(calc(-50% + ${panelPosition.x}px), calc(-50% + ${panelPosition.y}px))`
                            }}>
                                <div
                                    style={{
                                        ...productStyles.settingsHeader,
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        userSelect: 'none'
                                    }}
                                    onMouseDown={handleDragStart}
                                >
                                    <div>
                                        <span style={{fontWeight: 600, fontSize: '14px'}}>Product Settings</span>
                                        <span style={{fontSize: '11px', color: '#999', marginLeft: '8px'}}>drag to move</span>
                                    </div>
                                    <button onClick={() => setShowSettings(false)} style={productStyles.closeBtn}>×</button>
                                </div>

                                <div style={productStyles.settingsField}>
                                    <label style={productStyles.fieldLabel}>Product Name</label>
                                    <input
                                        type="text"
                                        className="settings-input"
                                        value={block.props.productName || ''}
                                        onChange={(e) => editor.updateBlock(block, {props: {...block.props, productName: e.target.value}})}
                                        style={productStyles.fieldInput}
                                        placeholder="Enter product name"
                                    />
                                </div>

                                <div style={productStyles.settingsField}>
                                    <label style={productStyles.fieldLabel}>Price ($)</label>
                                    <input
                                        type="text"
                                        className="settings-input"
                                        value={block.props.price || ''}
                                        onChange={(e) => editor.updateBlock(block, {props: {...block.props, price: e.target.value}})}
                                        onBlur={(e) => editor.updateBlock(block, {props: {...block.props, price: formatPrice(e.target.value)}})}
                                        style={productStyles.fieldInput}
                                        placeholder="0.00"
                                    />
                                </div>

                                <div style={productStyles.settingsField}>
                                    <label style={productStyles.fieldLabel}>Description</label>
                                    <textarea
                                        className="settings-textarea"
                                        value={block.props.description || ''}
                                        onChange={(e) => editor.updateBlock(block, {props: {...block.props, description: e.target.value}})}
                                        style={{...productStyles.fieldInput, minHeight: '60px', resize: 'vertical'}}
                                        placeholder="Product description..."
                                    />
                                </div>

                                {/* Only show affiliate/link options for manual products (not brand products) */}
                                {!block.props.productId ? (
                                    <>
                                        <div style={productStyles.settingsField}>
                                            <label style={{...productStyles.fieldLabel, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                                <input
                                                    type="checkbox"
                                                    checked={block.props.isAffiliate || false}
                                                    onChange={(e) => editor.updateBlock(block, {props: {...block.props, isAffiliate: e.target.checked}})}
                                                    style={{width: '16px', height: '16px'}}
                                                />
                                                This is an affiliate product
                                            </label>
                                        </div>

                                        {block.props.isAffiliate ? (
                                            <div style={productStyles.settingsField}>
                                                <label style={productStyles.fieldLabel}>Affiliate Link</label>
                                                <input
                                                    type="url"
                                                    className="settings-input"
                                                    value={block.props.affiliateUrl || ''}
                                                    onChange={(e) => editor.updateBlock(block, {props: {...block.props, affiliateUrl: e.target.value}})}
                                                    style={productStyles.fieldInput}
                                                    placeholder="https://affiliate-link.com/..."
                                                />
                                            </div>
                                        ) : (
                                            <div style={productStyles.settingsField}>
                                                <label style={productStyles.fieldLabel}>Shop URL</label>
                                                <input
                                                    type="url"
                                                    className="settings-input"
                                                    value={block.props.shopUrl || ''}
                                                    onChange={(e) => editor.updateBlock(block, {props: {...block.props, shopUrl: e.target.value}})}
                                                    style={productStyles.fieldInput}
                                                    placeholder="https://shop.com/product"
                                                />
                                            </div>
                                        )}

                                        <div style={productStyles.settingsField}>
                                            <label style={productStyles.fieldLabel}>Button Text</label>
                                            <input
                                                type="text"
                                                className="settings-input"
                                                value={block.props.buttonText || ''}
                                                onChange={(e) => editor.updateBlock(block, {props: {...block.props, buttonText: e.target.value}})}
                                                style={productStyles.fieldInput}
                                                placeholder="Shop Now"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    /* Brand product info */
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#f0f9ff',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        color: '#0369a1',
                                        marginBottom: '12px'
                                    }}>
                                        <strong>Brand Product</strong><br/>
                                        This product is linked to your brand catalog and can be purchased directly through the cart.
                                    </div>
                                )}

                                <button onClick={() => setShowSettings(false)} style={productStyles.doneBtn}>Done</button>
                            </div>
                        </>
                    )}

                    {/* Product Browser Modal */}
                    {showBrowser && (
                        <>
                            <div onClick={() => setShowBrowser(false)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000}} />
                            <div style={productStyles.browserModal}>
                                <div style={productStyles.browserHeader}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        {browserStep === 'products' && (
                                            <button onClick={() => {setBrowserStep('brands'); setSelectedBrand(null);}} style={productStyles.browserBackBtn}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                                            </button>
                                        )}
                                        <h3 style={{margin: 0, fontSize: '16px', fontWeight: 600}}>
                                            {browserStep === 'brands' ? 'Select a Brand' : selectedBrand?.name || 'Products'}
                                        </h3>
                                    </div>
                                    <button onClick={() => setShowBrowser(false)} style={productStyles.closeBtn}>×</button>
                                </div>

                                <div style={{padding: '0 16px 12px'}}>
                                    <input
                                        type="text"
                                        className="settings-input"
                                        value={browserSearch}
                                        onChange={(e) => setBrowserSearch(e.target.value)}
                                        placeholder={browserStep === 'brands' ? 'Search brands...' : 'Search products...'}
                                        style={productStyles.browserSearch}
                                    />
                                </div>

                                <div style={productStyles.browserContent}>
                                    {browserLoading ? (
                                        <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>Loading...</div>
                                    ) : browserStep === 'brands' ? (
                                        <div style={productStyles.browserGrid}>
                                            {filteredBrands.length === 0 ? (
                                                <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#999'}}>No brands found</div>
                                            ) : filteredBrands.map(brand => (
                                                <div key={brand.id} style={productStyles.browserCard} onClick={() => selectBrand(brand)}>
                                                    <div style={productStyles.browserCardImage}>
                                                        {brand.imageUrl ? <img src={brand.imageUrl} alt={brand.name} style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e5e7eb', color: '#6b7280', fontSize: '20px', fontWeight: 600}}>{brand.name?.charAt(0)}</div>}
                                                    </div>
                                                    <div style={productStyles.browserCardInfo}>
                                                        <div style={{fontWeight: 600, fontSize: '13px'}}>{brand.name}</div>
                                                        <div style={{fontSize: '11px', color: '#666'}}><span style={{color: '#fbbf24'}}>★</span> {brand.rating != null ? brand.rating.toFixed(1) : '0'} ({brand.reviews ?? 0})</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={productStyles.browserGrid}>
                                            {filteredProducts.length === 0 ? (
                                                <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px 20px'}}>
                                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" style={{marginBottom: '12px'}}>
                                                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                                    </svg>
                                                    <div style={{color: '#666', fontSize: '14px', fontWeight: 500, marginBottom: '8px'}}>No products synced yet</div>
                                                    <div style={{color: '#999', fontSize: '12px', lineHeight: '1.5'}}>
                                                        Go to <strong>Brands</strong> page, select this brand, and click <strong>"Import"</strong> to sync products from your store.
                                                    </div>
                                                </div>
                                            ) : filteredProducts.map(product => (
                                                <div key={product.id} style={productStyles.browserCard} onClick={() => selectProduct(product)}>
                                                    <div style={productStyles.browserCardImage}>
                                                        {product.imageUrl ? <img src={product.imageUrl} alt={product.title} style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', color: '#999', fontSize: '11px'}}>No image</div>}
                                                    </div>
                                                    <div style={productStyles.browserCardInfo}>
                                                        <div style={{fontWeight: 500, fontSize: '12px', marginBottom: '2px'}}>{product.title}</div>
                                                        <div style={{fontSize: '12px', fontWeight: 600, color: '#059669'}}>${product.retailPrice ? parseFloat(product.retailPrice).toFixed(2) : '0.00'}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </BlockToolbar>
            );
        }
    }
);

const productStyles = {
    container: {
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '8px 4px 8px 0',
        boxSizing: 'border-box',
        verticalAlign: 'top'
    },
    imageWrapper: {
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: '#f5f5f5'
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    uploadArea: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
    },
    info: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    brandTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 8px',
        backgroundColor: '#eff6ff',
        color: '#3b82f6',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 500,
        marginBottom: '4px',
        alignSelf: 'flex-start'
    },
    productName: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#1a1a1a',
        lineHeight: 1.3
    },
    productPrice: {
        fontSize: '14px',
        fontWeight: 700,
        color: '#059669'
    },
    productDesc: {
        fontSize: '12px',
        color: '#666',
        lineHeight: 1.4,
        marginTop: '4px'
    },
    shopButton: {
        alignSelf: 'flex-start',
        padding: '8px 20px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: '8px'
    },
    settingsBtn: {
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#f5f5f5',
        color: '#666',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: '8px'
    },
    settingsPanel: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '340px',
        maxWidth: '90vw',
        maxHeight: '85vh',
        overflowY: 'auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        padding: '20px',
        zIndex: 1001
    },
    settingsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #eee'
    },
    closeBtn: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    settingsField: {
        marginBottom: '12px'
    },
    fieldLabel: {
        display: 'block',
        fontSize: '12px',
        fontWeight: 500,
        color: '#666',
        marginBottom: '4px'
    },
    fieldInput: {
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        fontSize: '13px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        caretColor: '#000'
    },
    doneBtn: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: '8px'
    },
    browserModal: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '550px',
        maxWidth: '95vw',
        maxHeight: '80vh',
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    browserHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        borderBottom: '1px solid #e5e5e5'
    },
    browserBackBtn: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        border: '1px solid #e5e5e5',
        backgroundColor: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    browserSearch: {
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        caretColor: '#000'
    },
    browserContent: {
        flex: 1,
        overflow: 'auto',
        padding: '16px'
    },
    browserGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
        gap: '12px'
    },
    browserCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e5e5e5',
        cursor: 'pointer',
        transition: 'border-color 0.15s'
    },
    browserCardImage: {
        width: '100%',
        aspectRatio: '1',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
    },
    browserCardInfo: {
        padding: '10px'
    }
};

// ============================================
// LINK BUTTON BLOCK
// ============================================
export const LinkButtonBlock = createReactBlockSpec(
    {
        type: 'linkButton',
        propSchema: {
            text: {
                default: 'Button Text'
            },
            url: {
                default: ''
            },
            style: {
                default: 'filled'
            },
            size: {
                default: 'full',
                values: ['small', 'full']
            },
            alignment: {default: 'center'},
            buttonColor: {default: '#1a1a1a'},
            textColor: {default: '#ffffff'}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const isFilled = block.props.style === 'filled';
            const size = block.props.size || 'full';
            const isSmall = size === 'small';
            const isEditable = editor.isEditable;
            const buttonColor = block.props.buttonColor || '#1a1a1a';
            const textColor = block.props.textColor || '#ffffff';

            // State for settings panel
            const [showSettings, setShowSettings] = useState(false);

            // Color presets for button
            const colorPresets = [
                {color: '#1a1a1a', name: 'Black'},
                {color: '#292f4c', name: 'Navy'},
                {color: '#3b82f6', name: 'Blue'},
                {color: '#10b981', name: 'Green'},
                {color: '#ef4444', name: 'Red'},
                {color: '#f59e0b', name: 'Orange'},
                {color: '#8b5cf6', name: 'Purple'},
                {color: '#ec4899', name: 'Pink'}
            ];

            const buttonStyle = {
                ...linkButtonStyles.button,
                backgroundColor: isFilled ? buttonColor : 'transparent',
                color: isFilled ? textColor : buttonColor,
                border: isFilled ? 'none' : `2px solid ${buttonColor}`,
                padding: isSmall ? '10px 16px' : '14px 20px',
                fontSize: isSmall ? '13px' : '15px'
            };

            // Preview mode - clean display without controls
            if (!isEditable) {
                const ButtonContent = (
                    <div style={{...buttonStyle, cursor: block.props.url ? 'pointer' : 'default'}}>
                        {block.props.text || 'Button'}
                    </div>
                );

                return (
                    <div style={{
                        ...linkButtonStyles.container,
                        width: isSmall ? '48%' : '100%',
                        display: isSmall ? 'inline-block' : 'block'
                    }}>
                        {block.props.url ? (
                            <a href={block.props.url} target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
                                {ButtonContent}
                            </a>
                        ) : ButtonContent}
                    </div>
                );
            }

            // Editor mode - with controls
            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Style',
                            prop: 'style',
                            options: [
                                {value: 'filled', label: 'Filled'},
                                {value: 'outline', label: 'Outline'}
                            ]
                        },
                        {
                            label: 'Size',
                            prop: 'size',
                            options: [
                                {value: 'full', label: 'Full Width'},
                                {value: 'small', label: 'Half Width'}
                            ]
                        }
                    ]}
                >
                    <div style={{
                        ...linkButtonStyles.container,
                        width: isSmall ? '48%' : '100%',
                        display: isSmall ? 'inline-block' : 'block',
                        position: 'relative'
                    }}>
                        {/* Button display - click to open settings */}
                        <div
                            style={{...buttonStyle, cursor: 'pointer'}}
                            onClick={() => setShowSettings(true)}
                        >
                            {block.props.text || 'Button Text'}
                        </div>

                        {/* Settings Panel */}
                        {showSettings && (
                            <>
                                <div
                                    onClick={() => setShowSettings(false)}
                                    style={{
                                        position: 'fixed',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                        zIndex: 999
                                    }}
                                />
                                <div style={linkButtonStyles.settingsPanel}>
                                    <div style={linkButtonStyles.settingsHeader}>
                                        <span style={{fontWeight: 600, fontSize: '14px'}}>Button Settings</span>
                                        <button
                                            onClick={() => setShowSettings(false)}
                                            style={linkButtonStyles.closeBtn}
                                        >
                                            ×
                                        </button>
                                    </div>

                                    {/* Button Text */}
                                    <div style={linkButtonStyles.settingsField}>
                                        <label style={linkButtonStyles.fieldLabel}>Button Text</label>
                                        <input
                                            type="text"
                                            value={block.props.text || ''}
                                            onChange={(e) => {
                                                editor.updateBlock(block, {
                                                    props: {...block.props, text: e.target.value}
                                                });
                                            }}
                                            style={linkButtonStyles.fieldInput}
                                            placeholder="Enter button text"
                                            autoFocus
                                        />
                                    </div>

                                    {/* Button URL */}
                                    <div style={linkButtonStyles.settingsField}>
                                        <label style={linkButtonStyles.fieldLabel}>Link URL</label>
                                        <input
                                            type="url"
                                            value={block.props.url || ''}
                                            onChange={(e) => {
                                                editor.updateBlock(block, {
                                                    props: {...block.props, url: e.target.value}
                                                });
                                            }}
                                            style={linkButtonStyles.fieldInput}
                                            placeholder="https://example.com"
                                        />
                                    </div>

                                    {/* Button Color */}
                                    <div style={linkButtonStyles.settingsField}>
                                        <label style={linkButtonStyles.fieldLabel}>Button Color</label>
                                        <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                                            {colorPresets.map((preset) => (
                                                <button
                                                    key={preset.color}
                                                    onClick={() => {
                                                        editor.updateBlock(block, {
                                                            props: {...block.props, buttonColor: preset.color}
                                                        });
                                                    }}
                                                    title={preset.name}
                                                    style={{
                                                        width: '28px',
                                                        height: '28px',
                                                        borderRadius: '6px',
                                                        backgroundColor: preset.color,
                                                        border: buttonColor === preset.color ? '3px solid #3b82f6' : '2px solid #e0e0e0',
                                                        cursor: 'pointer',
                                                        padding: 0
                                                    }}
                                                />
                                            ))}
                                            {/* Custom color input */}
                                            <input
                                                type="color"
                                                value={buttonColor}
                                                onChange={(e) => {
                                                    editor.updateBlock(block, {
                                                        props: {...block.props, buttonColor: e.target.value}
                                                    });
                                                }}
                                                style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    borderRadius: '6px',
                                                    border: '2px solid #e0e0e0',
                                                    cursor: 'pointer',
                                                    padding: 0
                                                }}
                                                title="Custom color"
                                            />
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setShowSettings(false)}
                                        style={linkButtonStyles.doneBtn}
                                    >
                                        Done
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </BlockToolbar>
            );
        }
    }
);

const linkButtonStyles = {
    container: {
        margin: '8px 4px 8px 0',
        boxSizing: 'border-box',
        verticalAlign: 'top'
    },
    button: {
        width: '100%',
        borderRadius: '10px',
        fontWeight: 500,
        textAlign: 'center',
        cursor: 'text',
        outline: 'none',
        boxSizing: 'border-box'
    },
    settingsPanel: {
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: '8px',
        width: '300px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        padding: '16px',
        zIndex: 1000
    },
    settingsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #eee'
    },
    closeBtn: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    settingsField: {
        marginBottom: '12px'
    },
    fieldLabel: {
        display: 'block',
        fontSize: '12px',
        fontWeight: 500,
        color: '#666',
        marginBottom: '4px'
    },
    fieldInput: {
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        caretColor: '#000'
    },
    doneBtn: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: '8px'
    }
};

// ============================================
// SOCIAL LINKS BLOCK
// ============================================
// Icon library for social links - users can pick any of these
const SOCIAL_ICON_LIBRARY = {
    // Social Media
    instagram: {
        name: 'Instagram',
        color: '#E4405F',
        path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'
    },
    tiktok: {
        name: 'TikTok',
        color: '#000000',
        path: 'M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z'
    },
    youtube: {
        name: 'YouTube',
        color: '#FF0000',
        path: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'
    },
    twitter: {
        name: 'X (Twitter)',
        color: '#000000',
        path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'
    },
    facebook: {
        name: 'Facebook',
        color: '#1877F2',
        path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'
    },
    linkedin: {
        name: 'LinkedIn',
        color: '#0A66C2',
        path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'
    },
    snapchat: {
        name: 'Snapchat',
        color: '#FFFC00',
        path: 'M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.097.118.112.222.083.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z'
    },
    pinterest: {
        name: 'Pinterest',
        color: '#E60023',
        path: 'M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z'
    },
    twitch: {
        name: 'Twitch',
        color: '#9146FF',
        path: 'M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z'
    },
    discord: {
        name: 'Discord',
        color: '#5865F2',
        path: 'M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028 14.09 14.09 0 001.226-1.994.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z'
    },
    spotify: {
        name: 'Spotify',
        color: '#1DB954',
        path: 'M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z'
    },
    // Utility Icons
    website: {
        name: 'Website',
        color: '#4A90D9',
        path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z'
    },
    email: {
        name: 'Email',
        color: '#EA4335',
        path: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z'
    },
    phone: {
        name: 'Phone',
        color: '#34A853',
        path: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z'
    },
    whatsapp: {
        name: 'WhatsApp',
        color: '#25D366',
        path: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'
    },
    telegram: {
        name: 'Telegram',
        color: '#0088CC',
        path: 'M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z'
    },
    threads: {
        name: 'Threads',
        color: '#000000',
        path: 'M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.88-.73 2.082-1.146 3.48-1.206 1.007-.043 1.946.053 2.773.275-.01-.464-.068-.9-.2-1.3-.335-1.028-1.07-1.61-2.252-1.782-.882-.128-1.775-.025-2.66.307l-.679-1.887c1.156-.418 2.358-.605 3.574-.558 1.666.064 3.016.626 3.914 1.628.754.842 1.163 1.96 1.254 3.393.345.095.678.21.995.347 1.14.493 2.07 1.254 2.683 2.2.778 1.2 1.053 2.73.775 4.307-.324 1.83-1.27 3.373-2.814 4.59C18.194 23.24 15.544 24 12.186 24zm1.638-7.727c.86-.045 1.507-.32 1.924-.822.395-.473.648-1.15.757-2.017-.43-.112-.905-.185-1.406-.207-1.61-.07-2.66.404-2.752 1.242-.048.44.168.845.6 1.127.407.265.936.408 1.488.408.13 0 .26-.007.389-.02z'
    },
    music: {
        name: 'Music',
        color: '#FF5500',
        path: 'M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z'
    },
    shop: {
        name: 'Shop',
        color: '#FF6B6B',
        path: 'M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z'
    },
    location: {
        name: 'Location',
        color: '#FF9500',
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
    },
    calendar: {
        name: 'Calendar',
        color: '#007AFF',
        path: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z'
    },
    paypal: {
        name: 'PayPal',
        color: '#003087',
        path: 'M7.076 21.337H2.47a.641.641 0 01-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 00-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 00-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 00.554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 01.923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z'
    },
    venmo: {
        name: 'Venmo',
        color: '#008CFF',
        path: 'M19.292 0c.802 1.322 1.166 2.687 1.166 4.406 0 5.49-4.687 12.632-8.487 17.594H4.592L1.167 1.541l6.928-.635 1.869 14.993c1.738-2.83 3.886-7.272 3.886-10.316 0-1.618-.28-2.722-.756-3.617L19.292 0z'
    },
    cashapp: {
        name: 'Cash App',
        color: '#00D632',
        path: 'M23.59 3.47A5.1 5.1 0 0020.5.42C19.37.08 18 0 15.36 0h-6.7C5.98 0 4.64.08 3.51.42A5.1 5.1 0 00.41 3.5C.08 4.63 0 5.98 0 8.65v6.7c0 2.67.08 4.01.42 5.14a5.1 5.1 0 003.08 3.08c1.13.34 2.47.42 5.14.42h6.71c2.67 0 4.01-.08 5.14-.42a5.1 5.1 0 003.08-3.08c.34-1.13.42-2.47.42-5.14v-6.7c0-2.67-.08-4.02-.42-5.15l.02-.03zM17.42 8.81l-.93.93a.5.5 0 01-.67.01 5 5 0 00-3.22-1.18c-.97 0-1.94.32-1.94 1.21 0 .9 1.04 1.2 2.24 1.65 2.1.7 3.84 1.58 3.84 3.64 0 2.24-1.74 3.78-4.58 4.02v1.93a.5.5 0 01-.5.5H9.64a.5.5 0 01-.5-.5v-1.86c-1.5-.2-2.9-.75-3.93-1.62a.5.5 0 01-.03-.72l.93-.93a.5.5 0 01.67-.02 5.3 5.3 0 003.56 1.36c1.35 0 2.17-.53 2.17-1.42 0-.78-.95-1.18-2.19-1.66-2.05-.69-3.71-1.55-3.71-3.65 0-1.98 1.6-3.56 4.26-3.86V4.66c0-.28.22-.5.5-.5h2.02c.28 0 .5.22.5.5v1.76a6.5 6.5 0 013.36 1.3.5.5 0 01.02.72l-.87.87.02-.02-.02.01z'
    }
};

export const SocialLinksBlock = createReactBlockSpec(
    {
        type: 'socialLinks',
        propSchema: {
            // Links stored as JSON string: [{id, iconType, url, label}]
            links: {default: '[]'},
            align: {default: 'center', values: ['left', 'center', 'right']},
            iconSize: {default: 'medium', values: ['small', 'medium', 'large']},
            iconStyle: {default: 'filled', values: ['filled', 'outline']},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const align = block.props.align || 'center';
            const iconSize = block.props.iconSize || 'medium';
            const iconStyle = block.props.iconStyle || 'filled';
            const isEditable = editor.isEditable;

            // State for icon picker modal
            const [showIconPicker, setShowIconPicker] = useState(false);
            const [editingLink, setEditingLink] = useState(null);

            // Parse links from JSON string
            let links = [];
            try {
                links = JSON.parse(block.props.links || '[]');
            } catch (e) {
                links = [];
            }

            const updateLinks = (newLinks) => {
                editor.updateBlock(block, {
                    props: {...block.props, links: JSON.stringify(newLinks)}
                });
            };

            const addLink = (iconType) => {
                const iconInfo = SOCIAL_ICON_LIBRARY[iconType];
                const newLink = {
                    id: Date.now().toString(),
                    iconType: iconType,
                    url: '',
                    label: iconInfo?.name || iconType
                };
                updateLinks([...links, newLink]);
                setShowIconPicker(false);
                setEditingLink(newLink.id);
            };

            const updateLink = (id, field, value) => {
                const updated = links.map(link =>
                    link.id === id ? {...link, [field]: value} : link
                );
                updateLinks(updated);
            };

            const removeLink = (id) => {
                updateLinks(links.filter(link => link.id !== id));
                if (editingLink === id) {
                    setEditingLink(null);
                }
            };

            const alignmentStyles = {
                left: 'flex-start',
                center: 'center',
                right: 'flex-end'
            };

            const sizeMap = {
                small: 32,
                medium: 40,
                large: 48
            };
            const currentSize = sizeMap[iconSize];

            // Preview mode - clean display
            if (!isEditable) {
                if (links.length === 0) return null;
                return (
                    <div style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: alignmentStyles[align],
                        padding: '12px 0'
                    }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                            {links.map((link) => {
                                const iconInfo = SOCIAL_ICON_LIBRARY[link.iconType];
                                if (!iconInfo || !link.url) return null;
                                return (
                                    <a
                                        key={link.id}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            width: `${currentSize}px`,
                                            height: `${currentSize}px`,
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: iconStyle === 'filled' ? iconInfo.color : 'transparent',
                                            border: iconStyle === 'outline' ? `2px solid ${iconInfo.color}` : 'none',
                                            textDecoration: 'none',
                                            transition: 'transform 0.2s'
                                        }}
                                        title={link.label || iconInfo.name}
                                    >
                                        <svg
                                            width={currentSize * 0.45}
                                            height={currentSize * 0.45}
                                            viewBox="0 0 24 24"
                                            fill={iconStyle === 'filled' ? '#fff' : iconInfo.color}
                                        >
                                            <path d={iconInfo.path}/>
                                        </svg>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            // Editor mode
            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Align',
                            prop: 'align',
                            options: [
                                {value: 'left', label: 'Left'},
                                {value: 'center', label: 'Center'},
                                {value: 'right', label: 'Right'}
                            ]
                        },
                        {
                            label: 'Size',
                            prop: 'iconSize',
                            options: [
                                {value: 'small', label: 'Small'},
                                {value: 'medium', label: 'Medium'},
                                {value: 'large', label: 'Large'}
                            ]
                        },
                        {
                            label: 'Style',
                            prop: 'iconStyle',
                            options: [
                                {value: 'filled', label: 'Filled'},
                                {value: 'outline', label: 'Outline'}
                            ]
                        }
                    ]}
                >
                <div style={socialLinksStyles.container}>
                    {/* Icons row */}
                    <div style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: alignmentStyles[align],
                        flexWrap: 'wrap',
                        gap: '8px'
                    }}>
                        {links.map((link) => {
                            const iconInfo = SOCIAL_ICON_LIBRARY[link.iconType];
                            if (!iconInfo) return null;
                            const hasUrl = !!link.url;
                            return (
                                <div
                                    key={link.id}
                                    style={{
                                        position: 'relative',
                                        width: `${currentSize}px`,
                                        height: `${currentSize}px`,
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: iconStyle === 'filled'
                                            ? (hasUrl ? iconInfo.color : '#e0e0e0')
                                            : 'transparent',
                                        border: iconStyle === 'outline'
                                            ? `2px solid ${hasUrl ? iconInfo.color : '#ccc'}`
                                            : 'none',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s'
                                    }}
                                    onClick={() => setEditingLink(editingLink === link.id ? null : link.id)}
                                    title={`${link.label || iconInfo.name} - Click to edit`}
                                >
                                    <svg
                                        width={currentSize * 0.45}
                                        height={currentSize * 0.45}
                                        viewBox="0 0 24 24"
                                        fill={iconStyle === 'filled'
                                            ? (hasUrl ? '#fff' : '#999')
                                            : (hasUrl ? iconInfo.color : '#999')
                                        }
                                    >
                                        <path d={iconInfo.path}/>
                                    </svg>
                                    {/* Remove button */}
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeLink(link.id);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '-6px',
                                            right: '-6px',
                                            width: '18px',
                                            height: '18px',
                                            borderRadius: '50%',
                                            backgroundColor: '#ff4444',
                                            color: '#fff',
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            opacity: 0.8
                                        }}
                                        title="Remove"
                                    >
                                        ×
                                    </div>
                                </div>
                            );
                        })}

                        {/* Add button */}
                        <div
                            style={{
                                width: `${currentSize}px`,
                                height: `${currentSize}px`,
                                borderRadius: '10px',
                                border: '2px dashed #ccc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'border-color 0.2s, background-color 0.2s'
                            }}
                            onClick={() => setShowIconPicker(true)}
                            title="Add social link"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#999">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                            </svg>
                        </div>
                    </div>

                    {/* URL input for selected link */}
                    {editingLink && (
                        <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            {(() => {
                                const link = links.find(l => l.id === editingLink);
                                if (!link) return null;
                                const iconInfo = SOCIAL_ICON_LIBRARY[link.iconType];
                                return (
                                    <>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill={iconInfo?.color || '#666'}>
                                            <path d={iconInfo?.path || ''}/>
                                        </svg>
                                        <input
                                            type="url"
                                            value={link.url || ''}
                                            onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                                            style={{
                                                flex: 1,
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                padding: '8px 12px',
                                                fontSize: '13px',
                                                outline: 'none'
                                            }}
                                            placeholder={`Enter ${iconInfo?.name || 'link'} URL...`}
                                            autoFocus
                                        />
                                        <button
                                            onClick={() => setEditingLink(null)}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: '#1a1a1a',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Done
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {/* Icon Picker Modal */}
                    {showIconPicker && (
                        <>
                            <div
                                onClick={() => setShowIconPicker(false)}
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                    zIndex: 999
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginTop: '8px',
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                                padding: '16px',
                                zIndex: 1000,
                                width: '320px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    marginBottom: '12px',
                                    color: '#1a1a1a'
                                }}>
                                    Add Social Link
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(5, 1fr)',
                                    gap: '8px'
                                }}>
                                    {Object.entries(SOCIAL_ICON_LIBRARY).map(([key, icon]) => (
                                        <div
                                            key={key}
                                            onClick={() => addLink(key)}
                                            style={{
                                                width: '48px',
                                                height: '48px',
                                                borderRadius: '10px',
                                                backgroundColor: icon.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s'
                                            }}
                                            title={icon.name}
                                        >
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff">
                                                <path d={icon.path}/>
                                            </svg>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                </BlockToolbar>
            );
        }
    }
);

const socialLinksStyles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        margin: '8px 0',
        padding: '16px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        position: 'relative'
    },
    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        marginTop: '8px',
        paddingTop: '12px',
        borderTop: '1px solid #f0f0f0'
    },
    alignToggle: {
        display: 'flex',
        gap: '2px'
    },
    layoutBtn: {
        width: '24px',
        height: '24px',
        border: '1px solid #e5e5e5',
        borderRadius: '4px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
    },
    layoutBtnActive: {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderColor: '#1a1a1a'
    }
};

// ============================================
// PROFILE HEADER BLOCK
// ============================================
export const ProfileHeaderBlock = createReactBlockSpec(
    {
        type: 'profileHeader',
        propSchema: {
            name: {default: 'Your Name'},
            bio: {default: 'Your bio here'},
            avatarUrl: {default: ''},
            layout: {default: 'centered', values: ['centered', 'compact']},
            instagram: {default: ''},
            tiktok: {default: ''},
            youtube: {default: ''},
            twitter: {default: ''},
            facebook: {default: ''},
            linkedin: {default: ''},
            // Track which socials are visible in compact mode
            visibleSocials: {default: 'instagram,tiktok'},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const fileInputRef = useRef(null);
            const layout = block.props.layout || 'centered';
            const isCompact = layout === 'compact';

            const handleAvatarUpload = (e) => {
                const file = e.target.files?.[0];
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        editor.updateBlock(block, {
                            props: {...block.props, avatarUrl: reader.result}
                        });
                    };
                    reader.readAsDataURL(file);
                }
            };

            const socialIcons = {
                instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
                tiktok: 'M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z',
                youtube: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
                twitter: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
                facebook: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
                linkedin: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'
            };

            const socialColors = {
                instagram: '#E4405F',
                tiktok: '#000000',
                youtube: '#FF0000',
                twitter: '#000000',
                facebook: '#1877F2',
                linkedin: '#0A66C2'
            };

            const socialLabels = {
                instagram: 'Instagram',
                tiktok: 'TikTok',
                youtube: 'YouTube',
                twitter: 'X (Twitter)',
                facebook: 'Facebook',
                linkedin: 'LinkedIn'
            };

            // Parse visible socials from comma-separated string
            const visibleSocials = (block.props.visibleSocials || 'instagram,tiktok').split(',').filter(Boolean);

            const toggleSocialVisibility = (platform) => {
                const current = new Set(visibleSocials);
                if (current.has(platform)) {
                    current.delete(platform);
                } else {
                    current.add(platform);
                }
                editor.updateBlock(block, {
                    props: {...block.props, visibleSocials: Array.from(current).join(',')}
                });
            };

            // State for dropdown
            const [showSocialDropdown, setShowSocialDropdown] = useState(false);
            const [editingSocial, setEditingSocial] = useState(null);

            // Compact layout - avatar + name on left, social icons on right
            if (isCompact) {
                return (
                    <BlockToolbar
                        block={block}
                        editor={editor}
                        blockOptions={[
                            {
                                label: 'Layout',
                                prop: 'layout',
                                options: [
                                    {value: 'compact', label: 'Compact'},
                                    {value: 'centered', label: 'Centered'}
                                ]
                            }
                        ]}
                    >
                        <div style={profileHeaderStyles.compactContainer}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarUpload}
                            style={{display: 'none'}}
                        />

                        <div style={profileHeaderStyles.compactLeft}>
                            <div
                                style={profileHeaderStyles.compactAvatarWrapper}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {block.props.avatarUrl ? (
                                    <img src={block.props.avatarUrl} alt="" style={profileHeaderStyles.compactAvatar} />
                                ) : (
                                    <div style={profileHeaderStyles.compactAvatarPlaceholder}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#999">
                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <input
                                    type="text"
                                    value={block.props.name}
                                    onChange={(e) => {
                                        editor.updateBlock(block, {
                                            props: {...block.props, name: e.target.value}
                                        });
                                    }}
                                    style={profileHeaderStyles.compactNameInput}
                                    placeholder="Your Name"
                                />
                                <input
                                    type="text"
                                    value={block.props.bio}
                                    onChange={(e) => {
                                        editor.updateBlock(block, {
                                            props: {...block.props, bio: e.target.value}
                                        });
                                    }}
                                    style={profileHeaderStyles.compactBioInput}
                                    placeholder="Your bio here"
                                />
                            </div>
                        </div>

                        <div style={profileHeaderStyles.compactRight}>
                            {/* Render visible social icons */}
                            {visibleSocials.map((platform) => {
                                if (!socialIcons[platform]) return null;
                                const hasUrl = !!block.props[platform];
                                return (
                                    <div
                                        key={platform}
                                        style={{
                                            ...profileHeaderStyles.socialIconBtn,
                                            backgroundColor: hasUrl ? socialColors[platform] : '#f0f0f0'
                                        }}
                                        onClick={() => setEditingSocial(editingSocial === platform ? null : platform)}
                                        title={socialLabels[platform]}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill={hasUrl ? '#fff' : '#666'}>
                                            <path d={socialIcons[platform]}/>
                                        </svg>
                                    </div>
                                );
                            })}
                            {/* Add social dropdown button */}
                            <div style={{position: 'relative'}}>
                                <div
                                    style={profileHeaderStyles.addSocialBtn}
                                    onClick={() => setShowSocialDropdown(!showSocialDropdown)}
                                    title="Add or remove social icons"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#666">
                                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                    </svg>
                                </div>
                                {/* Dropdown menu */}
                                {showSocialDropdown && (
                                    <>
                                    {/* Backdrop to close dropdown when clicking outside */}
                                    <div
                                        onClick={() => setShowSocialDropdown(false)}
                                        style={{
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            zIndex: 99
                                        }}
                                    />
                                    <div style={profileHeaderStyles.socialDropdown} onClick={(e) => e.stopPropagation()}>
                                        {Object.keys(socialIcons).map((platform) => {
                                            const isActive = visibleSocials.includes(platform);
                                            return (
                                                <div
                                                    key={platform}
                                                    style={{
                                                        ...profileHeaderStyles.socialDropdownItem,
                                                        backgroundColor: isActive ? '#f0f0f0' : 'transparent'
                                                    }}
                                                    onClick={() => {
                                                        toggleSocialVisibility(platform);
                                                    }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill={socialColors[platform]}>
                                                        <path d={socialIcons[platform]}/>
                                                    </svg>
                                                    <span style={{flex: 1}}>{socialLabels[platform]}</span>
                                                    {isActive && (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#1a1a1a">
                                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                        </svg>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* URL input for selected social */}
                        {editingSocial && (
                            <div style={profileHeaderStyles.socialUrlInput}>
                                <span style={{fontSize: '12px', color: '#666'}}>{socialLabels[editingSocial]} URL:</span>
                                <input
                                    type="url"
                                    value={block.props[editingSocial] || ''}
                                    onChange={(e) => {
                                        editor.updateBlock(block, {props: {...block.props, [editingSocial]: e.target.value}});
                                    }}
                                    style={profileHeaderStyles.urlInputField}
                                    placeholder={`https://${editingSocial}.com/...`}
                                    autoFocus
                                />
                                <button
                                    onClick={() => setEditingSocial(null)}
                                    style={profileHeaderStyles.urlInputClose}
                                >
                                    ✓
                                </button>
                            </div>
                        )}
                    </div>
                    </BlockToolbar>
                );
            }

            // Centered layout (default)
            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Layout',
                            prop: 'layout',
                            options: [
                                {value: 'compact', label: 'Compact'},
                                {value: 'centered', label: 'Centered'}
                            ]
                        }
                    ]}
                >
                <div style={profileHeaderStyles.container}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        style={{display: 'none'}}
                    />

                    <div
                        style={profileHeaderStyles.avatarWrapper}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {block.props.avatarUrl ? (
                            <img src={block.props.avatarUrl} alt="" style={profileHeaderStyles.avatar} />
                        ) : (
                            <div style={profileHeaderStyles.avatarPlaceholder}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="#999">
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                            </div>
                        )}
                        <div style={profileHeaderStyles.editBadge}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                        </div>
                    </div>

                    <input
                        type="text"
                        value={block.props.name}
                        onChange={(e) => {
                            editor.updateBlock(block, {
                                props: {...block.props, name: e.target.value}
                            });
                        }}
                        style={profileHeaderStyles.nameInput}
                        placeholder="Your Name"
                    />

                    <textarea
                        value={block.props.bio}
                        onChange={(e) => {
                            editor.updateBlock(block, {
                                props: {...block.props, bio: e.target.value}
                            });
                        }}
                        style={profileHeaderStyles.bioInput}
                        placeholder="Your bio here"
                        rows={2}
                    />

                </div>
                </BlockToolbar>
            );
        }
    }
);

const profileHeaderStyles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        margin: '16px 0',
        padding: '20px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        position: 'relative'
    },
    avatarWrapper: {
        position: 'relative',
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        overflow: 'visible',
        cursor: 'pointer',
        marginBottom: '16px'
    },
    avatar: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        objectFit: 'cover'
    },
    avatarPlaceholder: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    editBadge: {
        position: 'absolute',
        bottom: '0',
        right: '0',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #fff'
    },
    nameInput: {
        border: 'none',
        fontSize: '24px',
        fontWeight: 700,
        textAlign: 'center',
        outline: 'none',
        backgroundColor: 'transparent',
        marginBottom: '8px',
        width: '100%'
    },
    bioInput: {
        border: 'none',
        fontSize: '14px',
        color: '#666',
        textAlign: 'center',
        outline: 'none',
        backgroundColor: 'transparent',
        resize: 'none',
        width: '100%',
        maxWidth: '300px'
    },
    // Compact layout styles
    compactContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: '8px 0',
        padding: '12px 16px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        position: 'relative'
    },
    compactLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1
    },
    compactAvatarWrapper: {
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        overflow: 'hidden',
        cursor: 'pointer',
        flexShrink: 0
    },
    compactAvatar: {
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        objectFit: 'cover'
    },
    compactAvatarPlaceholder: {
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    compactNameInput: {
        border: 'none',
        fontSize: '16px',
        fontWeight: 600,
        outline: 'none',
        backgroundColor: 'transparent',
        flex: 1,
        minWidth: 0
    },
    compactBioInput: {
        border: 'none',
        fontSize: '13px',
        color: '#666',
        outline: 'none',
        backgroundColor: 'transparent',
        marginTop: '2px',
        width: '100%'
    },
    compactRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginRight: '50px'
    },
    socialIconBtn: {
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    addSocialBtn: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        border: '2px dashed #ccc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        transition: 'border-color 0.2s, background-color 0.2s'
    },
    layoutToggle: {
        display: 'flex',
        gap: '2px',
        position: 'absolute',
        top: '50%',
        right: '8px',
        transform: 'translateY(-50%)'
    },
    layoutBtn: {
        width: '24px',
        height: '24px',
        border: '1px solid #e5e5e5',
        borderRadius: '4px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
    },
    layoutBtnActive: {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderColor: '#1a1a1a'
    },
    socialDropdown: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        padding: '8px',
        zIndex: 100,
        minWidth: '180px'
    },
    socialDropdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#1a1a1a',
        transition: 'background-color 0.15s'
    },
    socialUrlInput: {
        position: 'absolute',
        bottom: '-48px',
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 10
    },
    urlInputField: {
        flex: 1,
        border: '1px solid #e5e5e5',
        borderRadius: '6px',
        padding: '6px 10px',
        fontSize: '13px',
        outline: 'none'
    },
    urlInputClose: {
        width: '28px',
        height: '28px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '14px'
    }
};

// ============================================
// SHOPPABLE VIDEO BLOCK
// ============================================
export const ShoppableVideoBlock = createReactBlockSpec(
    {
        type: 'shoppableVideo',
        propSchema: {
            title: {default: 'Shoppable Video Post'},
            videoUrl: {default: ''},
            thumbnailUrl: {default: ''},
            itemCount: {default: '3'},
            size: {default: 'full', values: ['small', 'full']},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const thumbnailInputRef = useRef(null);
            const size = block.props.size || 'full';
            const isSmall = size === 'small';

            const handleThumbnailUpload = (e) => {
                const file = e.target.files?.[0];
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        editor.updateBlock(block, {
                            props: {...block.props, thumbnailUrl: reader.result}
                        });
                    };
                    reader.readAsDataURL(file);
                }
            };

            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Size',
                            prop: 'size',
                            options: [
                                {value: 'full', label: 'Full Width'},
                                {value: 'small', label: 'Half Width'}
                            ]
                        }
                    ]}
                >
                <div style={{
                    ...shoppableVideoStyles.container,
                    width: isSmall ? '48%' : '100%',
                    display: isSmall ? 'inline-block' : 'block'
                }}>
                    <div style={shoppableVideoStyles.header}>
                        <input
                            type="text"
                            value={block.props.title}
                            onChange={(e) => {
                                editor.updateBlock(block, {
                                    props: {...block.props, title: e.target.value}
                                });
                            }}
                            style={shoppableVideoStyles.titleInput}
                            placeholder="Video Title"
                        />
                    </div>

                    <input
                        ref={thumbnailInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        style={{display: 'none'}}
                    />

                    <div
                        style={{
                            ...shoppableVideoStyles.videoArea,
                            minHeight: isSmall ? '120px' : '200px'
                        }}
                        onClick={() => thumbnailInputRef.current?.click()}
                    >
                        {block.props.thumbnailUrl ? (
                            <>
                                <img src={block.props.thumbnailUrl} alt="" style={shoppableVideoStyles.thumbnail} />
                                <div style={shoppableVideoStyles.playOverlay}>
                                    <div style={shoppableVideoStyles.playButton}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                                            <path d="M8 5v14l11-7z"/>
                                        </svg>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div style={shoppableVideoStyles.uploadPlaceholder}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                                    <polygon points="23 7 16 12 23 17 23 7"/>
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                                </svg>
                                <span style={{fontSize: isSmall ? '11px' : '13px', color: '#999', marginTop: '8px'}}>
                                    Click to upload thumbnail
                                </span>
                            </div>
                        )}

                        {/* Shop Items Badge */}
                        <div style={shoppableVideoStyles.shopBadge}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4H6zM3 6h18M16 10a4 4 0 01-8 0"/>
                            </svg>
                            <span>Shop {block.props.itemCount} Items</span>
                        </div>
                    </div>

                    <div style={shoppableVideoStyles.footer}>
                        <input
                            type="url"
                            value={block.props.videoUrl}
                            onChange={(e) => {
                                editor.updateBlock(block, {
                                    props: {...block.props, videoUrl: e.target.value}
                                });
                            }}
                            style={shoppableVideoStyles.urlInput}
                            placeholder="Video URL (optional)"
                        />
                        <div style={shoppableVideoStyles.itemCountRow}>
                            <label style={{fontSize: '12px', color: '#666'}}>Items:</label>
                            <input
                                type="number"
                                value={block.props.itemCount}
                                onChange={(e) => {
                                    editor.updateBlock(block, {
                                        props: {...block.props, itemCount: e.target.value}
                                    });
                                }}
                                style={shoppableVideoStyles.itemCountInput}
                                min="1"
                                max="99"
                            />
                        </div>
                    </div>
                </div>
                </BlockToolbar>
            );
        }
    }
);

const shoppableVideoStyles = {
    container: {
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '8px 4px 8px 0',
        boxSizing: 'border-box',
        verticalAlign: 'top'
    },
    header: {
        padding: '10px 12px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
    },
    titleInput: {
        flex: 1,
        border: 'none',
        fontSize: '14px',
        fontWeight: 600,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        minWidth: 0
    },
    sizeToggle: {
        display: 'flex',
        gap: '2px',
        flexShrink: 0
    },
    sizeBtn: {
        width: '22px',
        height: '22px',
        border: '1px solid #e5e5e5',
        borderRadius: '4px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '11px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
    },
    sizeBtnActive: {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderColor: '#1a1a1a'
    },
    videoArea: {
        position: 'relative',
        width: '100%',
        backgroundColor: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        overflow: 'hidden'
    },
    thumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        position: 'absolute',
        top: 0,
        left: 0
    },
    playOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.3)'
    },
    playButton: {
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)'
    },
    uploadPlaceholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
    },
    shopBadge: {
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: '#fff',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 500,
        backdropFilter: 'blur(4px)'
    },
    footer: {
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    urlInput: {
        width: '100%',
        padding: '8px 10px',
        border: '1px solid #e5e5e5',
        borderRadius: '6px',
        fontSize: '12px',
        outline: 'none',
        backgroundColor: '#fafafa',
        boxSizing: 'border-box'
    },
    itemCountRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    itemCountInput: {
        width: '60px',
        padding: '6px 8px',
        border: '1px solid #e5e5e5',
        borderRadius: '6px',
        fontSize: '12px',
        outline: 'none',
        textAlign: 'center'
    }
};

// ============================================
// SECTION TITLE BLOCK
// ============================================
export const SectionTitleBlock = createReactBlockSpec(
    {
        type: 'sectionTitle',
        propSchema: {
            title: {default: 'Section Title'},
            layout: {default: 'column', values: ['column', 'row']},
            align: {default: 'left', values: ['left', 'center', 'right']},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const layout = block.props.layout || 'column';
            const align = block.props.align || 'left';
            const isRow = layout === 'row';

            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Align',
                            prop: 'align',
                            options: [
                                {value: 'left', label: 'Left'},
                                {value: 'center', label: 'Center'},
                                {value: 'right', label: 'Right'}
                            ]
                        },
                        {
                            label: 'Layout',
                            prop: 'layout',
                            options: [
                                {value: 'column', label: 'Stack'},
                                {value: 'row', label: 'Side by Side'}
                            ]
                        }
                    ]}
                >
                <div style={sectionTitleStyles.container}>
                    <div style={sectionTitleStyles.titleRow}>
                        <input
                            type="text"
                            value={block.props.title}
                            onChange={(e) => {
                                editor.updateBlock(block, {
                                    props: {...block.props, title: e.target.value}
                                });
                            }}
                            style={{
                                ...sectionTitleStyles.titleInput,
                                textAlign: align
                            }}
                            placeholder="Enter section title..."
                        />
                    </div>
                </div>
                </BlockToolbar>
            );
        }
    }
);

const sectionTitleStyles = {
    container: {
        margin: '16px 0 8px 0',
        padding: '12px 16px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px'
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px'
    },
    titleInput: {
        flex: 1,
        border: 'none',
        fontSize: '18px',
        fontWeight: 700,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        minWidth: 0
    },
    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0
    },
    toggleGroup: {
        display: 'flex',
        gap: '2px'
    },
    toggleBtn: {
        width: '26px',
        height: '26px',
        border: '1px solid #e5e5e5',
        borderRadius: '6px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
    },
    toggleBtnActive: {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderColor: '#1a1a1a'
    }
};

// ============================================
// GALLERY BLOCK - 2 Column Product Grid
// ============================================
export const GalleryBlock = createReactBlockSpec(
    {
        type: 'gallery',
        propSchema: {
            title: {default: 'Gallery View'},
            // Items stored as JSON string: [{id, name, price, description, imageUrl, link, affiliateUrl, isAffiliate, productId, brandSlug}]
            items: {default: '[]'},
            columns: {default: '2', values: ['2', '3', '4']},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'},
            showDescription: {default: 'true'},
            _triggerBrowse: {default: null}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const title = block.props.title || 'Gallery View';
            const columns = parseInt(block.props.columns || '2', 10);
            const showDescription = block.props.showDescription !== 'false';
            const isEditable = editor.isEditable;

            // Global cart for brand products (preview mode)
            const {addToCart, isInCart} = useCart();

            // State for editing item settings
            const [editingItemId, setEditingItemId] = useState(null);

            // Draggable panel state
            const [panelPosition, setPanelPosition] = useState({x: 0, y: 0});
            const [isDragging, setIsDragging] = useState(false);
            const dragOffsetRef = React.useRef({x: 0, y: 0});

            // Brand browser states
            const [showBrandBrowser, setShowBrandBrowser] = useState(false);
            const [brandBrowserItemId, setBrandBrowserItemId] = useState(null);
            const [brands, setBrands] = useState([]);
            const [brandsLoading, setBrandsLoading] = useState(false);
            const [selectedBrand, setSelectedBrand] = useState(null);
            const [brandProducts, setBrandProducts] = useState([]);
            const [productsLoading, setProductsLoading] = useState(false);

            const handleDragStart = (e) => {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
                setIsDragging(true);
                const rect = e.currentTarget.parentElement.getBoundingClientRect();
                dragOffsetRef.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };
                e.preventDefault();
            };

            // Use document-level event listeners for smooth dragging
            React.useEffect(() => {
                if (!isDragging) return;

                const handleMouseMove = (e) => {
                    const newX = e.clientX - dragOffsetRef.current.x - window.innerWidth / 2 + 160;
                    const newY = e.clientY - dragOffsetRef.current.y - window.innerHeight / 2;
                    setPanelPosition({x: newX, y: newY});
                };

                const handleMouseUp = () => {
                    setIsDragging(false);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);

                return () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                };
            }, [isDragging]);

            const openSettings = (itemId) => {
                setPanelPosition({x: 0, y: 0});
                setEditingItemId(itemId);
            };

            // Brand browser functions
            const openBrandBrowser = async (itemId) => {
                setBrandBrowserItemId(itemId);
                setShowBrandBrowser(true);
                setSelectedBrand(null);
                setBrandProducts([]);
                setBrandsLoading(true);
                try {
                    const result = await fetchBrands({limit: 50});
                    if (result.ok && result.data?.brands) {
                        setBrands(result.data.brands);
                    }
                } catch (err) {
                    console.error('Failed to fetch brands:', err);
                }
                setBrandsLoading(false);
            };

            // Watch for _triggerBrowse prop change from side menu
            useEffect(() => {
                console.log('Gallery _triggerBrowse effect:', block.props._triggerBrowse);
                if (block.props._triggerBrowse) {
                    console.log('Gallery: Opening brand browser from side menu');
                    openBrandBrowser(null);
                    // Clear the trigger prop
                    editor.updateBlock(block, {
                        props: {...block.props, _triggerBrowse: null}
                    });
                }
            }, [block.props._triggerBrowse]);

            const handleSelectBrand = async (brand) => {
                setSelectedBrand(brand);
                setProductsLoading(true);
                try {
                    const result = await fetchBrandProducts({slug: brand.slug, limit: 50});
                    console.log('Gallery - Fetched products:', result.data?.products);
                    if (result.ok && result.data?.products) {
                        setBrandProducts(result.data.products);
                    }
                } catch (err) {
                    console.error('Failed to fetch products:', err);
                }
                setProductsLoading(false);
            };

            const handleSelectBrandProduct = (product) => {
                // API returns title/retailPrice, map to our name/price fields
                const productName = product.title || product.name || 'Product';
                const productPrice = (product.retailPrice || product.price) ? parseFloat(product.retailPrice || product.price).toFixed(2) : '0.00';
                const productImage = product.imageUrl || product.images?.[0] || '';
                const productDesc = product.description || '';

                if (brandBrowserItemId) {
                    // Update existing item with brand product data
                    const updated = items.map(item =>
                        item.id === brandBrowserItemId ? {
                            ...item,
                            name: productName,
                            price: productPrice,
                            description: productDesc,
                            imageUrl: productImage,
                            productId: product.id,
                            brandSlug: selectedBrand?.slug || '',
                            isAffiliate: false,
                            link: '',
                            affiliateUrl: ''
                        } : item
                    );
                    updateItems(updated);
                } else {
                    // Add as new item
                    const newItem = {
                        id: Date.now().toString(),
                        name: productName,
                        price: productPrice,
                        description: productDesc,
                        imageUrl: productImage,
                        productId: product.id,
                        brandSlug: selectedBrand?.slug || '',
                        isAffiliate: false,
                        link: '',
                        affiliateUrl: ''
                    };
                    updateItems([...items, newItem]);
                }
                closeBrandBrowser();
            };

            const closeBrandBrowser = () => {
                setShowBrandBrowser(false);
                setBrandBrowserItemId(null);
                setSelectedBrand(null);
                setBrandProducts([]);
            };

            // Parse items from JSON string
            let items = [];
            try {
                items = JSON.parse(block.props.items || '[]');
            } catch (e) {
                items = [];
            }

            const updateItems = (newItems) => {
                editor.updateBlock(block, {
                    props: {...block.props, items: JSON.stringify(newItems)}
                });
            };

            const addItem = () => {
                const newItem = {
                    id: Date.now().toString(),
                    name: 'Product Name',
                    price: '0.00',
                    description: '',
                    imageUrl: '',
                    link: '',
                    affiliateUrl: '',
                    isAffiliate: false,
                    productId: null,
                    brandSlug: ''
                };
                updateItems([...items, newItem]);
            };

            const updateItem = (id, field, value) => {
                const updated = items.map(item =>
                    item.id === id ? {...item, [field]: value} : item
                );
                updateItems(updated);
            };

            // Format price to 2 decimal places
            const formatPrice = (value) => {
                const cleaned = value.replace(/[^0-9.]/g, '');
                const num = parseFloat(cleaned) || 0;
                return num.toFixed(2);
            };

            const handlePriceBlur = (id, value) => {
                const formatted = formatPrice(value);
                updateItem(id, 'price', formatted);
            };

            const removeItem = (id) => {
                updateItems(items.filter(item => item.id !== id));
            };

            const handleImageUpload = (id, e) => {
                const file = e.target.files?.[0];
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        updateItem(id, 'imageUrl', reader.result);
                    };
                    reader.readAsDataURL(file);
                }
            };

            // Check if item is a brand product
            const isBrandProduct = (item) => !!item.productId;

            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Columns',
                            prop: 'columns',
                            options: [
                                {value: '2', label: '2 Columns'},
                                {value: '3', label: '3 Columns'},
                                {value: '4', label: '4 Columns'}
                            ]
                        },
                        {
                            label: 'Description',
                            prop: 'showDescription',
                            options: [
                                {value: 'true', label: 'Show'},
                                {value: 'false', label: 'Hide'}
                            ]
                        }
                    ]}
                    blockActions={[
                        {
                            label: 'Browse Products from Brands',
                            icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>',
                            onClick: () => openBrandBrowser(null)
                        }
                    ]}
                >
                <div style={galleryStyles.container}>
                    {/* Header */}
                    <div style={galleryStyles.header}>
                        <div style={galleryStyles.titleRow}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#666" style={{flexShrink: 0}}>
                                <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/>
                            </svg>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => {
                                    editor.updateBlock(block, {
                                        props: {...block.props, title: e.target.value}
                                    });
                                }}
                                style={galleryStyles.titleInput}
                                placeholder="Gallery title..."
                            />
                        </div>
                    </div>

                    {/* Grid */}
                    <div style={{
                        ...galleryStyles.grid,
                        gridTemplateColumns: `repeat(${columns}, 1fr)`
                    }}>
                        {items.map((item) => {
                            const itemLink = item.isAffiliate ? item.affiliateUrl : item.link;
                            return (
                            <div key={item.id} style={galleryStyles.item}>
                                {/* Image */}
                                {isEditable ? (
                                    <div
                                        style={galleryStyles.imageArea}
                                        onClick={() => document.getElementById(`gallery-img-${item.id}`)?.click()}
                                    >
                                        <input
                                            id={`gallery-img-${item.id}`}
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageUpload(item.id, e)}
                                            style={{display: 'none'}}
                                        />
                                        {item.imageUrl ? (
                                            <img src={item.imageUrl} alt="" style={galleryStyles.image} />
                                        ) : (
                                            <div style={galleryStyles.imagePlaceholder}>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                                    <polyline points="21,15 16,10 5,21"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <a
                                        href={itemLink || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{...galleryStyles.imageArea, display: 'block', textDecoration: 'none'}}
                                    >
                                        {item.imageUrl ? (
                                            <img src={item.imageUrl} alt="" style={galleryStyles.image} />
                                        ) : (
                                            <div style={galleryStyles.imagePlaceholder}>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                                    <polyline points="21,15 16,10 5,21"/>
                                                </svg>
                                            </div>
                                        )}
                                    </a>
                                )}
                                {/* Info */}
                                <div style={galleryStyles.itemInfo}>
                                    {isEditable ? (
                                        <>
                                            <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                                                style={galleryStyles.nameInput}
                                                placeholder="Name"
                                            />
                                            <input
                                                type="text"
                                                value={item.price || ''}
                                                onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                                                onBlur={(e) => handlePriceBlur(item.id, e.target.value)}
                                                style={galleryStyles.priceInput}
                                                placeholder="0.00"
                                            />
                                            {showDescription && (
                                                <textarea
                                                    value={item.description || ''}
                                                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                                    style={galleryStyles.descriptionInput}
                                                    placeholder="Product description..."
                                                    rows={2}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div style={{...galleryStyles.nameInput, cursor: 'default'}}>{item.name}</div>
                                            <div style={{...galleryStyles.priceInput, cursor: 'default'}}>${item.price || '0.00'}</div>
                                            {showDescription && item.description && (
                                                <div style={galleryStyles.descriptionText}>{item.description}</div>
                                            )}
                                            {/* Add to Cart button for brand products in preview mode */}
                                            {isBrandProduct(item) && (
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (!isInCart(item.productId, item.productId)) {
                                                            addToCart({
                                                                id: item.productId,
                                                                productId: item.productId,
                                                                name: item.name,
                                                                price: parseFloat(item.price) || 0,
                                                                imageUrl: item.imageUrl,
                                                                brandSlug: item.brandSlug
                                                            });
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        marginTop: '8px',
                                                        backgroundColor: isInCart(item.productId, item.productId) ? '#22c55e' : '#1a1a1a',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        fontWeight: 500,
                                                        cursor: isInCart(item.productId, item.productId) ? 'default' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    {isInCart(item.productId, item.productId) ? (
                                                        <>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <polyline points="20,6 9,17 4,12"/>
                                                            </svg>
                                                            In Cart
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                                            </svg>
                                                            Add to Cart
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Brand product badge */}
                                {isBrandProduct(item) && (
                                    <div style={galleryStyles.brandBadge}>Brand Product</div>
                                )}

                                {/* Affiliate badge */}
                                {item.isAffiliate && !isBrandProduct(item) && (
                                    <div style={galleryStyles.affiliateBadge}>Affiliate</div>
                                )}

                                {/* Settings button (edit mode only) */}
                                {isEditable && (
                                    <button
                                        onClick={() => openSettings(item.id)}
                                        style={galleryStyles.settingsBtn}
                                        title="Product Settings"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="3"/>
                                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                        </svg>
                                    </button>
                                )}

                                {/* Remove button */}
                                {isEditable && (
                                    <button
                                        onClick={() => removeItem(item.id)}
                                        style={galleryStyles.removeBtn}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                )}

                                {/* Item Settings Panel */}
                                {isEditable && editingItemId === item.id && (
                                    <>
                                        <div
                                            onClick={() => !isDragging && setEditingItemId(null)}
                                            style={{
                                                position: 'fixed',
                                                top: 0, left: 0, right: 0, bottom: 0,
                                                backgroundColor: 'rgba(0,0,0,0.2)',
                                                zIndex: 999,
                                                cursor: isDragging ? 'grabbing' : 'default'
                                            }}
                                        />
                                        <div
                                            style={{
                                                ...galleryStyles.settingsPanel,
                                                transform: `translate(calc(-50% + ${panelPosition.x}px), calc(-50% + ${panelPosition.y}px))`
                                            }}
                                        >
                                            <div
                                                style={{
                                                    ...galleryStyles.settingsHeader,
                                                    cursor: isDragging ? 'grabbing' : 'grab',
                                                    userSelect: 'none'
                                                }}
                                                onMouseDown={handleDragStart}
                                            >
                                                <span style={{fontWeight: 600, fontSize: '14px'}}>Product Settings</span>
                                                <span style={{fontSize: '11px', color: '#999', marginLeft: '8px'}}>drag to move</span>
                                                <button
                                                    onClick={() => setEditingItemId(null)}
                                                    style={{...galleryStyles.closeBtn, marginLeft: 'auto'}}
                                                >
                                                    ×
                                                </button>
                                            </div>

                                            {/* Browse from Brands button */}
                                            <button
                                                onClick={() => {
                                                    setEditingItemId(null);
                                                    openBrandBrowser(item.id);
                                                }}
                                                style={{
                                                    ...galleryStyles.doneBtn,
                                                    backgroundColor: '#3b82f6',
                                                    marginBottom: '16px'
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px', verticalAlign: 'middle'}}>
                                                    <circle cx="11" cy="11" r="8"/>
                                                    <path d="M21 21l-4.35-4.35"/>
                                                </svg>
                                                Browse from Brands
                                            </button>

                                            {/* Product Name */}
                                            <div style={galleryStyles.settingsField}>
                                                <label style={galleryStyles.fieldLabel}>Product Name</label>
                                                <input
                                                    type="text"
                                                    className="settings-input"
                                                    value={item.name || ''}
                                                    onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                                                    style={galleryStyles.fieldInput}
                                                    placeholder="Product name"
                                                />
                                            </div>

                                            {/* Price */}
                                            <div style={galleryStyles.settingsField}>
                                                <label style={galleryStyles.fieldLabel}>Price</label>
                                                <input
                                                    type="text"
                                                    className="settings-input"
                                                    value={item.price || ''}
                                                    onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                                                    onBlur={(e) => handlePriceBlur(item.id, e.target.value)}
                                                    style={galleryStyles.fieldInput}
                                                    placeholder="0.00"
                                                />
                                            </div>

                                            {/* Description */}
                                            <div style={galleryStyles.settingsField}>
                                                <label style={galleryStyles.fieldLabel}>Description</label>
                                                <textarea
                                                    className="settings-textarea"
                                                    value={item.description || ''}
                                                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                                    style={{...galleryStyles.fieldInput, minHeight: '60px', resize: 'vertical'}}
                                                    placeholder="Product description..."
                                                    rows={3}
                                                />
                                            </div>

                                            {/* Divider */}
                                            <div style={{borderTop: '1px solid #eee', margin: '16px 0'}} />

                                            {/* Only show affiliate options for non-brand products */}
                                            {!isBrandProduct(item) && (
                                                <>
                                                    {/* Affiliate Toggle */}
                                                    <div style={galleryStyles.settingsField}>
                                                        <label style={{...galleryStyles.fieldLabel, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                                            <input
                                                                type="checkbox"
                                                                checked={item.isAffiliate || false}
                                                                onChange={(e) => updateItem(item.id, 'isAffiliate', e.target.checked)}
                                                                style={{width: '16px', height: '16px'}}
                                                            />
                                                            This is an affiliate link
                                                        </label>
                                                    </div>

                                                    {/* Affiliate URL - shown only if affiliate */}
                                                    {item.isAffiliate ? (
                                                        <div style={galleryStyles.settingsField}>
                                                            <label style={galleryStyles.fieldLabel}>Affiliate Link</label>
                                                            <input
                                                                type="url"
                                                                className="settings-input"
                                                                value={item.affiliateUrl || ''}
                                                                onChange={(e) => updateItem(item.id, 'affiliateUrl', e.target.value)}
                                                                style={galleryStyles.fieldInput}
                                                                placeholder="https://affiliate-link.com/..."
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={galleryStyles.settingsField}>
                                                            <label style={galleryStyles.fieldLabel}>Link URL</label>
                                                            <input
                                                                type="url"
                                                                className="settings-input"
                                                                value={item.link || ''}
                                                                onChange={(e) => updateItem(item.id, 'link', e.target.value)}
                                                                style={galleryStyles.fieldInput}
                                                                placeholder="https://example.com/product"
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Brand product info */}
                                            {isBrandProduct(item) && (
                                                <div style={{
                                                    padding: '12px',
                                                    backgroundColor: '#f0f9ff',
                                                    borderRadius: '8px',
                                                    fontSize: '12px',
                                                    color: '#0369a1'
                                                }}>
                                                    <strong>Brand Product</strong><br/>
                                                    This product is linked to your brand catalog and can be purchased directly.
                                                </div>
                                            )}

                                            <button
                                                onClick={() => setEditingItemId(null)}
                                                style={galleryStyles.doneBtn}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );})}

                        {/* Add item button */}
                        <div style={galleryStyles.addItem} onClick={addItem}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="#999">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                            </svg>
                            <span>Add Item</span>
                        </div>
                    </div>

                    {/* Brand Browser Modal */}
                    {showBrandBrowser && (
                        <>
                            <div
                                onClick={closeBrandBrowser}
                                style={{
                                    position: 'fixed',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    zIndex: 1000
                                }}
                            />
                            <div style={galleryStyles.brandBrowserModal}>
                                <div style={galleryStyles.brandBrowserHeader}>
                                    <span style={{fontWeight: 600, fontSize: '16px'}}>
                                        {selectedBrand ? `Products from ${selectedBrand.name}` : 'Select a Brand'}
                                    </span>
                                    {selectedBrand && (
                                        <button
                                            onClick={() => {
                                                setSelectedBrand(null);
                                                setBrandProducts([]);
                                            }}
                                            style={{
                                                padding: '4px 12px',
                                                backgroundColor: '#f5f5f5',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                marginLeft: '12px'
                                            }}
                                        >
                                            Back to Brands
                                        </button>
                                    )}
                                    <button
                                        onClick={closeBrandBrowser}
                                        style={{...galleryStyles.closeBtn, marginLeft: 'auto'}}
                                    >
                                        ×
                                    </button>
                                </div>

                                <div style={galleryStyles.brandBrowserContent}>
                                    {/* Brands List */}
                                    {!selectedBrand && (
                                        brandsLoading ? (
                                            <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>Loading brands...</div>
                                        ) : brands.length === 0 ? (
                                            <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>No brands found</div>
                                        ) : (
                                            <div style={galleryStyles.brandGrid}>
                                                {brands.map(brand => (
                                                    <div
                                                        key={brand.id}
                                                        style={galleryStyles.brandCard}
                                                        onClick={() => handleSelectBrand(brand)}
                                                    >
                                                        <div style={galleryStyles.brandCardImage}>
                                                            {brand.imageUrl ? (
                                                                <img src={brand.imageUrl} alt={brand.name} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                                            ) : (
                                                                <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', color: '#999', fontSize: '24px', fontWeight: 600}}>
                                                                    {brand.name?.charAt(0) || '?'}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{padding: '8px', fontSize: '13px', fontWeight: 500, textAlign: 'center'}}>{brand.name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}

                                    {/* Products List */}
                                    {selectedBrand && (
                                        productsLoading ? (
                                            <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>Loading products...</div>
                                        ) : brandProducts.length === 0 ? (
                                            <div style={{textAlign: 'center', padding: '40px 20px'}}>
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" style={{marginBottom: '12px'}}>
                                                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                                </svg>
                                                <div style={{color: '#666', fontSize: '14px', fontWeight: 500, marginBottom: '8px'}}>No products synced yet</div>
                                                <div style={{color: '#999', fontSize: '12px', lineHeight: '1.5'}}>
                                                    Go to <strong>Brands</strong> page, select this brand, and click <strong>"Import"</strong> to sync products from your store.
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={galleryStyles.productGrid}>
                                                {brandProducts.map(product => (
                                                    <div
                                                        key={product.id}
                                                        style={galleryStyles.productCard}
                                                        onClick={() => handleSelectBrandProduct(product)}
                                                    >
                                                        <div style={galleryStyles.productCardImage}>
                                                            {(product.imageUrl || product.images?.[0]) ? (
                                                                <img src={product.imageUrl || product.images?.[0]} alt={product.title} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                                            ) : (
                                                                <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', color: '#bbb'}}>
                                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                                        <circle cx="8.5" cy="8.5" r="1.5"/>
                                                                        <polyline points="21,15 16,10 5,21"/>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{padding: '8px'}}>
                                                            <div style={{fontSize: '13px', fontWeight: 500, marginBottom: '4px'}}>{product.title}</div>
                                                            {product.retailPrice && (
                                                                <div style={{fontSize: '12px', color: '#666'}}>${parseFloat(product.retailPrice).toFixed(2)}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                </BlockToolbar>
            );
        }
    }
);

const galleryStyles = {
    container: {
        margin: '8px 0',
        padding: '16px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        gap: '12px'
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1
    },
    titleInput: {
        flex: 1,
        border: 'none',
        fontSize: '16px',
        fontWeight: 600,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        padding: 0
    },
    columnControls: {
        display: 'flex',
        gap: '2px'
    },
    colBtn: {
        width: '26px',
        height: '26px',
        border: '1px solid #e5e5e5',
        borderRadius: '6px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
    },
    colBtnActive: {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderColor: '#1a1a1a'
    },
    grid: {
        display: 'grid',
        gap: '12px'
    },
    item: {
        position: 'relative',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        overflow: 'hidden'
    },
    itemInfo: {
        padding: '0'
    },
    imageArea: {
        width: '100%',
        aspectRatio: '1',
        cursor: 'pointer',
        overflow: 'hidden'
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    imagePlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0f0f0'
    },
    nameInput: {
        width: '100%',
        padding: '8px',
        border: 'none',
        fontSize: '13px',
        fontWeight: 600,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        boxSizing: 'border-box'
    },
    priceInput: {
        width: '100%',
        padding: '0 8px 4px',
        border: 'none',
        fontSize: '12px',
        color: '#666',
        outline: 'none',
        backgroundColor: 'transparent',
        boxSizing: 'border-box'
    },
    descriptionInput: {
        width: '100%',
        padding: '4px 8px 8px',
        border: 'none',
        fontSize: '11px',
        color: '#888',
        outline: 'none',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        resize: 'none',
        lineHeight: 1.4
    },
    descriptionText: {
        width: '100%',
        padding: '4px 8px 8px',
        fontSize: '11px',
        color: '#888',
        lineHeight: 1.4
    },
    removeBtn: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        color: '#fff',
        border: 'none',
        fontSize: '14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.7
    },
    addItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        minHeight: '150px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        border: '2px dashed #ddd',
        cursor: 'pointer',
        color: '#999',
        fontSize: '12px'
    },
    affiliateBadge: {
        position: 'absolute',
        top: '4px',
        left: '4px',
        fontSize: '9px',
        fontWeight: 600,
        color: '#fff',
        backgroundColor: '#10b981',
        padding: '2px 6px',
        borderRadius: '4px',
        zIndex: 2
    },
    brandBadge: {
        position: 'absolute',
        top: '4px',
        left: '4px',
        fontSize: '9px',
        fontWeight: 600,
        color: '#fff',
        backgroundColor: '#3b82f6',
        padding: '2px 6px',
        borderRadius: '4px',
        zIndex: 2
    },
    settingsBtn: {
        position: 'absolute',
        top: '4px',
        right: '28px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.7,
        zIndex: 2
    },
    settingsPanel: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '320px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflowY: 'auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        padding: '20px',
        zIndex: 1001
    },
    settingsHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #eee'
    },
    closeBtn: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    settingsField: {
        marginBottom: '12px'
    },
    fieldLabel: {
        display: 'block',
        fontSize: '12px',
        fontWeight: 500,
        color: '#666',
        marginBottom: '4px'
    },
    fieldInput: {
        width: '100%',
        padding: '8px 10px',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        fontSize: '13px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        caretColor: '#000'
    },
    doneBtn: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: '8px'
    },
    // Brand Browser Modal styles
    brandBrowserModal: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    brandBrowserHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px',
        borderBottom: '1px solid #eee'
    },
    brandBrowserContent: {
        flex: 1,
        overflowY: 'auto',
        padding: '16px'
    },
    brandGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: '12px'
    },
    brandCard: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e5e5e5',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s'
    },
    brandCardImage: {
        width: '100%',
        aspectRatio: '1',
        overflow: 'hidden'
    },
    productGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: '12px'
    },
    productCard: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e5e5e5',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s'
    },
    productCardImage: {
        width: '100%',
        aspectRatio: '1',
        overflow: 'hidden'
    }
};

// ============================================
// PRODUCT GALLERY BLOCK (Swipeable Row)
// ============================================
export const ProductGalleryBlock = createReactBlockSpec(
    {
        type: 'productGallery',
        propSchema: {
            title: {default: 'Top Picks'},
            // Items stored as JSON string: [{id, name, price, imageUrl, buttonText, buttonUrl}]
            items: {default: '[]'},
            buttonStyle: {default: 'filled', values: ['filled', 'outline']},
            layout: {default: 'row', values: ['row', 'grid']},
            alignment: {default: 'center'},
            backgroundColor: {default: '#fff'},
            textColor: {default: '#1a1a1a'},
            _triggerBrowse: {default: null}
        },
        content: 'none'
    },
    {
        render: (props) => {
            const {block, editor} = props;
            const title = block.props.title || 'Top Picks';
            const buttonStyle = block.props.buttonStyle || 'filled';
            const layout = block.props.layout || 'row';
            const isGrid = layout === 'grid';
            const isEditable = editor.isEditable;

            // Global cart for brand products (preview mode)
            const {addToCart, isInCart} = useCart();

            // State for editing individual products
            const [editingProductId, setEditingProductId] = useState(null);

            // Draggable panel state
            const [panelPosition, setPanelPosition] = useState({x: 0, y: 0});
            const [isDragging, setIsDragging] = useState(false);
            const dragOffsetRef = React.useRef({x: 0, y: 0});

            const handleDragStart = (e) => {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
                setIsDragging(true);
                const rect = e.currentTarget.parentElement.getBoundingClientRect();
                dragOffsetRef.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };
                e.preventDefault();
            };

            // Use document-level event listeners for smooth dragging
            React.useEffect(() => {
                if (!isDragging) return;

                const handleMouseMove = (e) => {
                    const newX = e.clientX - dragOffsetRef.current.x - window.innerWidth / 2 + 160;
                    const newY = e.clientY - dragOffsetRef.current.y - window.innerHeight / 2;
                    setPanelPosition({x: newX, y: newY});
                };

                const handleMouseUp = () => {
                    setIsDragging(false);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);

                return () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                };
            }, [isDragging]);

            // Reset position when opening new panel
            const openSettings = (itemId) => {
                setPanelPosition({x: 0, y: 0});
                setEditingProductId(itemId);
            };

            // Product Browser State
            const [showBrowser, setShowBrowser] = useState(false);
            const [browserStep, setBrowserStep] = useState('brands'); // 'brands' or 'products'
            const [brands, setBrands] = useState([]);
            const [selectedBrand, setSelectedBrand] = useState(null);
            const [brandProducts, setBrandProducts] = useState([]);
            const [browserLoading, setBrowserLoading] = useState(false);
            const [browserSearch, setBrowserSearch] = useState('');
            const [selectedProducts, setSelectedProducts] = useState([]);

            // Load brands when browser opens
            const loadBrands = useCallback(async () => {
                setBrowserLoading(true);
                const result = await fetchBrands({limit: 50});
                if (result.ok && result.data) {
                    setBrands(result.data.brands || []);
                }
                setBrowserLoading(false);
            }, []);

            // Load products for selected brand
            const loadBrandProducts = useCallback(async (brandSlug) => {
                setBrowserLoading(true);
                const result = await fetchBrandProducts({slug: brandSlug, limit: 50});
                if (result.ok && result.data) {
                    setBrandProducts(result.data.products || []);
                }
                setBrowserLoading(false);
            }, []);

            // Open browser
            const openBrowser = () => {
                setShowBrowser(true);
                setBrowserStep('brands');
                setSelectedBrand(null);
                setBrandProducts([]);
                setSelectedProducts([]);
                setBrowserSearch('');
                loadBrands();
            };

            // Watch for _triggerBrowse prop change from side menu
            useEffect(() => {
                if (block.props._triggerBrowse) {
                    openBrowser();
                    // Clear the trigger prop
                    editor.updateBlock(block, {
                        props: {...block.props, _triggerBrowse: null}
                    });
                }
            }, [block.props._triggerBrowse]);

            // Select a brand and load its products
            const selectBrand = (brand) => {
                setSelectedBrand(brand);
                setBrowserStep('products');
                loadBrandProducts(brand.slug);
            };

            // Go back to brands
            const goBackToBrands = () => {
                setBrowserStep('brands');
                setSelectedBrand(null);
                setBrandProducts([]);
                setSelectedProducts([]);
            };

            // Toggle product selection
            const toggleProductSelection = (product) => {
                setSelectedProducts(prev => {
                    const exists = prev.find(p => p.id === product.id);
                    if (exists) {
                        return prev.filter(p => p.id !== product.id);
                    } else {
                        return [...prev, product];
                    }
                });
            };

            // Add selected products to gallery
            const addSelectedProducts = () => {
                const newItems = selectedProducts.map(product => ({
                    id: Date.now().toString() + '-' + product.id,
                    name: product.title || 'Product',
                    price: product.retailPrice ? parseFloat(product.retailPrice).toFixed(2) : '0.00',
                    imageUrl: product.imageUrl || '',
                    buttonText: 'Shop Now',
                    buttonUrl: product.url || '',
                    affiliateUrl: product.affiliateUrl || '',
                    isAffiliate: !!product.affiliateUrl,
                    description: product.description || '',
                    brandName: selectedBrand?.name || '',
                    productId: product.id
                }));

                const currentItems = JSON.parse(block.props.items || '[]');
                editor.updateBlock(block, {
                    props: {...block.props, items: JSON.stringify([...currentItems, ...newItems])}
                });

                setShowBrowser(false);
                setSelectedProducts([]);
            };

            // Filter brands/products by search
            const filteredBrands = brands.filter(b =>
                b.name?.toLowerCase().includes(browserSearch.toLowerCase())
            );
            const filteredProducts = brandProducts.filter(p =>
                p.title?.toLowerCase().includes(browserSearch.toLowerCase())
            );

            // Check if product is from brands (has productId)
            const isBrandProduct = (item) => !!item.productId;

            // Parse items from JSON string
            let items = [];
            try {
                items = JSON.parse(block.props.items || '[]');
            } catch (e) {
                items = [];
            }

            const updateItems = (newItems) => {
                editor.updateBlock(block, {
                    props: {...block.props, items: JSON.stringify(newItems)}
                });
            };

            const addItem = () => {
                const newItem = {
                    id: Date.now().toString(),
                    name: 'Product Name',
                    price: '0.00',
                    imageUrl: '',
                    buttonText: 'Add to Cart',
                    buttonUrl: '',
                    affiliateUrl: '',
                    isAffiliate: false,
                    description: ''
                };
                updateItems([...items, newItem]);
                setEditingProductId(newItem.id);
            };

            const updateItem = (id, field, value) => {
                const updated = items.map(item =>
                    item.id === id ? {...item, [field]: value} : item
                );
                updateItems(updated);
            };

            // Format price to 2 decimal places
            const formatPrice = (value) => {
                const num = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
                return num.toFixed(2);
            };

            const handlePriceChange = (id, value) => {
                // Allow typing, format on blur
                updateItem(id, 'price', value);
            };

            const handlePriceBlur = (id, value) => {
                const formatted = formatPrice(value);
                updateItem(id, 'price', formatted);
            };

            const removeItem = (id) => {
                updateItems(items.filter(item => item.id !== id));
            };

            const handleImageUpload = (id, e) => {
                const file = e.target.files?.[0];
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        updateItem(id, 'imageUrl', reader.result);
                    };
                    reader.readAsDataURL(file);
                }
            };

            const isFilled = buttonStyle === 'filled';

            return (
                <BlockToolbar
                    block={block}
                    editor={editor}
                    blockOptions={[
                        {
                            label: 'Layout',
                            prop: 'layout',
                            options: [
                                {value: 'row', label: 'Row'},
                                {value: 'grid', label: 'Grid'}
                            ]
                        },
                        {
                            label: 'Button Style',
                            prop: 'buttonStyle',
                            options: [
                                {value: 'filled', label: 'Filled'},
                                {value: 'outline', label: 'Outline'}
                            ]
                        }
                    ]}
                    blockActions={[
                        {
                            label: 'Browse Products from Brands',
                            icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>',
                            color: '#3b82f6',
                            onClick: openBrowser
                        }
                    ]}
                >
                <div style={productGalleryStyles.container}>
                    {/* Header */}
                    <div style={productGalleryStyles.header}>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => {
                                editor.updateBlock(block, {
                                    props: {...block.props, title: e.target.value}
                                });
                            }}
                            style={productGalleryStyles.titleInput}
                            placeholder="Section title..."
                        />
                    </div>

                    {/* Products Container - Row or Grid based on layout */}
                    <div style={isGrid ? productGalleryStyles.gridContainer : productGalleryStyles.scrollContainer}>
                        <div style={isGrid ? productGalleryStyles.gridLayout : productGalleryStyles.scrollRow}>
                            {items.map((item) => (
                                <div key={item.id} style={productGalleryStyles.productCard}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageUpload(item.id, e)}
                                        style={{display: 'none'}}
                                        id={`pgimg-${item.id}`}
                                    />

                                    {/* Settings & Remove buttons */}
                                    {isEditable && (
                                        <div style={productGalleryStyles.cardActions}>
                                            <button
                                                onClick={() => setEditingProductId(editingProductId === item.id ? null : item.id)}
                                                style={{
                                                    ...productGalleryStyles.actionBtn,
                                                    backgroundColor: editingProductId === item.id ? '#1a1a1a' : '#fff'
                                                }}
                                                title="Product settings"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill={editingProductId === item.id ? '#fff' : '#666'}>
                                                    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => removeItem(item.id)}
                                                style={productGalleryStyles.removeBtn}
                                                title="Remove product"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    )}

                                    {/* Image */}
                                    <div
                                        style={productGalleryStyles.imageArea}
                                        onClick={() => isEditable && document.getElementById(`pgimg-${item.id}`)?.click()}
                                    >
                                        {item.imageUrl ? (
                                            <img src={item.imageUrl} alt={item.name} style={productGalleryStyles.image} />
                                        ) : (
                                            <div style={productGalleryStyles.imagePlaceholder}>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                                    <polyline points="21,15 16,10 5,21"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    {/* Product Info - simplified view */}
                                    <div style={productGalleryStyles.productInfo}>
                                        <div style={productGalleryStyles.productName}>{item.name || 'Product Name'}</div>
                                        <div style={productGalleryStyles.productPrice}>${item.price || '0.00'}</div>
                                        {item.isAffiliate && (
                                            <div style={productGalleryStyles.affiliateBadge}>Affiliate</div>
                                        )}
                                    </div>

                                    {/* Button - Different for Brand Products vs Affiliate */}
                                    {!isEditable ? (
                                        /* Preview/Read mode */
                                        isBrandProduct(item) ? (
                                            /* Brand Product - Add to Cart button */
                                            isInCart(item.productId, item.productId) ? (
                                                <div
                                                    style={{
                                                        ...productGalleryStyles.actionButton,
                                                        backgroundColor: '#10b981',
                                                        color: '#fff',
                                                        border: 'none',
                                                        cursor: 'default'
                                                    }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{marginRight: '4px', verticalAlign: 'middle'}}>
                                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                    </svg>
                                                    In Cart
                                                </div>
                                            ) : (
                                                <div
                                                    style={{
                                                        ...productGalleryStyles.actionButton,
                                                        backgroundColor: isFilled ? '#3b82f6' : 'transparent',
                                                        color: isFilled ? '#fff' : '#3b82f6',
                                                        border: isFilled ? 'none' : '2px solid #3b82f6',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => addToCart({
                                                        id: item.productId,
                                                        productId: item.productId,
                                                        name: item.name,
                                                        price: parseFloat(item.price) || 0,
                                                        imageUrl: item.imageUrl,
                                                        brandSlug: item.brandSlug || item.brandName
                                                    })}
                                                >
                                                    Add to Cart
                                                </div>
                                            )
                                        ) : (
                                            /* Affiliate/Manual Product - Link button */
                                            <a
                                                href={item.isAffiliate ? item.affiliateUrl : item.buttonUrl || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    ...productGalleryStyles.actionButton,
                                                    backgroundColor: isFilled ? '#ff6b6b' : 'transparent',
                                                    color: isFilled ? '#fff' : '#ff6b6b',
                                                    border: isFilled ? 'none' : '2px solid #ff6b6b',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {item.buttonText || (item.isAffiliate ? 'Shop Now' : 'View Product')}
                                            </a>
                                        )
                                    ) : (
                                        /* Edit mode - Settings button */
                                        <div
                                            style={{
                                                ...productGalleryStyles.actionButton,
                                                backgroundColor: isBrandProduct(item)
                                                    ? (isFilled ? '#3b82f6' : 'transparent')
                                                    : (isFilled ? '#ff6b6b' : 'transparent'),
                                                color: isBrandProduct(item)
                                                    ? (isFilled ? '#fff' : '#3b82f6')
                                                    : (isFilled ? '#fff' : '#ff6b6b'),
                                                border: isBrandProduct(item)
                                                    ? (isFilled ? 'none' : '2px solid #3b82f6')
                                                    : (isFilled ? 'none' : '2px solid #ff6b6b'),
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => openSettings(item.id)}
                                        >
                                            {isBrandProduct(item) ? 'Add to Cart' : (item.buttonText || 'Shop Now')}
                                        </div>
                                    )}

                                    {/* Brand Product Badge */}
                                    {isBrandProduct(item) && (
                                        <div style={productGalleryStyles.brandBadge}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                                            </svg>
                                            {item.brandName || 'Brand'}
                                        </div>
                                    )}

                                    {/* Product Settings Panel */}
                                    {isEditable && editingProductId === item.id && (
                                        <>
                                            <div
                                                onClick={() => !isDragging && setEditingProductId(null)}
                                                style={{
                                                    position: 'fixed',
                                                    top: 0, left: 0, right: 0, bottom: 0,
                                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                                    zIndex: 999,
                                                    cursor: isDragging ? 'grabbing' : 'default'
                                                }}
                                            />
                                            <div
                                                style={{
                                                    ...productGalleryStyles.settingsPanel,
                                                    transform: `translate(calc(-50% + ${panelPosition.x}px), calc(-50% + ${panelPosition.y}px))`,
                                                    cursor: isDragging ? 'grabbing' : 'default'
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        ...productGalleryStyles.settingsHeader,
                                                        cursor: isDragging ? 'grabbing' : 'grab',
                                                        userSelect: 'none'
                                                    }}
                                                    onMouseDown={handleDragStart}
                                                >
                                                    <span style={{fontWeight: 600, fontSize: '14px'}}>Product Settings</span>
                                                    <span style={{fontSize: '11px', color: '#999', marginLeft: '8px'}}>drag to move</span>
                                                    <button
                                                        onClick={() => setEditingProductId(null)}
                                                        style={{...productGalleryStyles.closeBtn, marginLeft: 'auto'}}
                                                    >
                                                        ×
                                                    </button>
                                                </div>

                                                {/* Product Name */}
                                                <div style={productGalleryStyles.settingsField}>
                                                    <label style={productGalleryStyles.fieldLabel}>Product Name</label>
                                                    <input
                                                        type="text"
                                                        className="settings-input"
                                                        value={item.name || ''}
                                                        onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                                                        style={productGalleryStyles.fieldInput}
                                                        placeholder="Enter product name"
                                                    />
                                                </div>

                                                {/* Price */}
                                                <div style={productGalleryStyles.settingsField}>
                                                    <label style={productGalleryStyles.fieldLabel}>Price ($)</label>
                                                    <input
                                                        type="text"
                                                        className="settings-input"
                                                        value={item.price || ''}
                                                        onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                                        onBlur={(e) => handlePriceBlur(item.id, e.target.value)}
                                                        style={productGalleryStyles.fieldInput}
                                                        placeholder="0.00"
                                                    />
                                                </div>

                                                {/* Description */}
                                                <div style={productGalleryStyles.settingsField}>
                                                    <label style={productGalleryStyles.fieldLabel}>Description (optional)</label>
                                                    <textarea
                                                        className="settings-textarea"
                                                        value={item.description || ''}
                                                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                                        style={{...productGalleryStyles.fieldInput, minHeight: '60px', resize: 'vertical'}}
                                                        placeholder="Product description..."
                                                    />
                                                </div>

                                                {/* Only show affiliate/link options for manual products (not brand products) */}
                                                {!item.productId ? (
                                                    <>
                                                        {/* Affiliate Toggle */}
                                                        <div style={productGalleryStyles.settingsField}>
                                                            <label style={{...productGalleryStyles.fieldLabel, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={item.isAffiliate || false}
                                                                    onChange={(e) => updateItem(item.id, 'isAffiliate', e.target.checked)}
                                                                    style={{width: '16px', height: '16px'}}
                                                                />
                                                                This is an affiliate product
                                                            </label>
                                                        </div>

                                                        {/* Affiliate URL - shown only if affiliate */}
                                                        {item.isAffiliate && (
                                                            <div style={productGalleryStyles.settingsField}>
                                                                <label style={productGalleryStyles.fieldLabel}>Affiliate Link</label>
                                                                <input
                                                                    type="url"
                                                                    className="settings-input"
                                                                    value={item.affiliateUrl || ''}
                                                                    onChange={(e) => updateItem(item.id, 'affiliateUrl', e.target.value)}
                                                                    style={productGalleryStyles.fieldInput}
                                                                    placeholder="https://affiliate-link.com/..."
                                                                />
                                                            </div>
                                                        )}

                                                        {/* Button Text */}
                                                        <div style={productGalleryStyles.settingsField}>
                                                            <label style={productGalleryStyles.fieldLabel}>Button Text</label>
                                                            <input
                                                                type="text"
                                                                className="settings-input"
                                                                value={item.buttonText || ''}
                                                                onChange={(e) => updateItem(item.id, 'buttonText', e.target.value)}
                                                                style={productGalleryStyles.fieldInput}
                                                                placeholder="Add to Cart"
                                                            />
                                                        </div>

                                                        {/* Button URL - shown only if not affiliate */}
                                                        {!item.isAffiliate && (
                                                            <div style={productGalleryStyles.settingsField}>
                                                                <label style={productGalleryStyles.fieldLabel}>Button URL</label>
                                                                <input
                                                                    type="url"
                                                                    className="settings-input"
                                                                    value={item.buttonUrl || ''}
                                                                    onChange={(e) => updateItem(item.id, 'buttonUrl', e.target.value)}
                                                                    style={productGalleryStyles.fieldInput}
                                                                    placeholder="https://shop.com/product"
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    /* Brand product info */
                                                    <div style={{
                                                        padding: '12px',
                                                        backgroundColor: '#f0f9ff',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        color: '#0369a1',
                                                        marginBottom: '12px'
                                                    }}>
                                                        <strong>Brand Product</strong><br/>
                                                        This product is linked to your brand catalog and can be purchased directly through the cart.
                                                    </div>
                                                )}

                                                <button
                                                    onClick={() => setEditingProductId(null)}
                                                    style={productGalleryStyles.doneBtn}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}

                            {/* Add Product Buttons */}
                            {isEditable && (
                                <>
                                    <div
                                        style={productGalleryStyles.addCard}
                                        onClick={addItem}
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                                            <line x1="12" y1="5" x2="12" y2="19"/>
                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                        </svg>
                                        <span>Add Manual</span>
                                    </div>
                                    <div
                                        style={{...productGalleryStyles.addCard, backgroundColor: '#f0f9ff', borderColor: '#3b82f6'}}
                                        onClick={openBrowser}
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                                            <circle cx="11" cy="11" r="8"/>
                                            <path d="M21 21l-4.35-4.35"/>
                                        </svg>
                                        <span style={{color: '#3b82f6'}}>Browse Brands</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Product Browser Modal */}
                {showBrowser && (
                    <>
                        <div
                            onClick={() => setShowBrowser(false)}
                            style={{
                                position: 'fixed',
                                top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                zIndex: 1000
                            }}
                        />
                        <div style={productGalleryStyles.browserModal}>
                            {/* Browser Header */}
                            <div style={productGalleryStyles.browserHeader}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                    {browserStep === 'products' && (
                                        <button
                                            onClick={goBackToBrands}
                                            style={productGalleryStyles.browserBackBtn}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                                            </svg>
                                        </button>
                                    )}
                                    <h3 style={{margin: 0, fontSize: '16px', fontWeight: 600}}>
                                        {browserStep === 'brands' ? 'Select a Brand' : selectedBrand?.name || 'Products'}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setShowBrowser(false)}
                                    style={productGalleryStyles.closeBtn}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Search */}
                            <div style={{padding: '0 16px 12px'}}>
                                <input
                                    type="text"
                                    className="settings-input"
                                    value={browserSearch}
                                    onChange={(e) => setBrowserSearch(e.target.value)}
                                    placeholder={browserStep === 'brands' ? 'Search brands...' : 'Search products...'}
                                    style={productGalleryStyles.browserSearch}
                                />
                            </div>

                            {/* Content */}
                            <div style={productGalleryStyles.browserContent}>
                                {browserLoading ? (
                                    <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                                        Loading...
                                    </div>
                                ) : browserStep === 'brands' ? (
                                    /* Brands List */
                                    <div style={productGalleryStyles.browserGrid}>
                                        {filteredBrands.length === 0 ? (
                                            <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#999'}}>
                                                No brands found
                                            </div>
                                        ) : filteredBrands.map(brand => (
                                            <div
                                                key={brand.id}
                                                style={productGalleryStyles.browserCard}
                                                onClick={() => selectBrand(brand)}
                                            >
                                                <div style={productGalleryStyles.browserCardImage}>
                                                    {brand.imageUrl ? (
                                                        <img src={brand.imageUrl} alt={brand.name} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                                    ) : (
                                                        <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e5e7eb', color: '#6b7280', fontSize: '20px', fontWeight: 600}}>
                                                            {brand.name?.charAt(0) || '?'}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={productGalleryStyles.browserCardInfo}>
                                                    <div style={{fontWeight: 600, fontSize: '13px', color: '#1a1a1a'}}>{brand.name}</div>
                                                    <div style={{fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                                        <span style={{color: '#fbbf24'}}>★</span>
                                                        {brand.rating != null ? brand.rating.toFixed(1) : '0'} ({brand.reviews ?? 0})
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* Products List */
                                    <div style={productGalleryStyles.browserGrid}>
                                        {filteredProducts.length === 0 ? (
                                            <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px 20px'}}>
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" style={{marginBottom: '12px'}}>
                                                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                                </svg>
                                                <div style={{color: '#666', fontSize: '14px', fontWeight: 500, marginBottom: '8px'}}>No products synced yet</div>
                                                <div style={{color: '#999', fontSize: '12px', lineHeight: '1.5'}}>
                                                    Go to <strong>Brands</strong> page, select this brand, and click <strong>"Import"</strong> to sync products from your store.
                                                </div>
                                            </div>
                                        ) : filteredProducts.map(product => {
                                            const isSelected = selectedProducts.some(p => p.id === product.id);
                                            return (
                                                <div
                                                    key={product.id}
                                                    style={{
                                                        ...productGalleryStyles.browserCard,
                                                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e5e5',
                                                        backgroundColor: isSelected ? '#eff6ff' : '#fff'
                                                    }}
                                                    onClick={() => toggleProductSelection(product)}
                                                >
                                                    {isSelected && (
                                                        <div style={productGalleryStyles.selectedBadge}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                            </svg>
                                                        </div>
                                                    )}
                                                    <div style={productGalleryStyles.browserCardImage}>
                                                        {product.imageUrl ? (
                                                            <img src={product.imageUrl} alt={product.title} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                                        ) : (
                                                            <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', color: '#999', fontSize: '11px'}}>
                                                                No image
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={productGalleryStyles.browserCardInfo}>
                                                        <div style={{fontWeight: 500, fontSize: '12px', color: '#1a1a1a', lineHeight: 1.3, marginBottom: '4px'}}>{product.title}</div>
                                                        <div style={{fontSize: '12px', fontWeight: 600, color: '#059669'}}>
                                                            ${product.retailPrice ? parseFloat(product.retailPrice).toFixed(2) : '0.00'}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Footer with Add button */}
                            {browserStep === 'products' && selectedProducts.length > 0 && (
                                <div style={productGalleryStyles.browserFooter}>
                                    <span style={{fontSize: '13px', color: '#666'}}>
                                        {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
                                    </span>
                                    <button
                                        onClick={addSelectedProducts}
                                        style={productGalleryStyles.browserAddBtn}
                                    >
                                        Add to Gallery
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}

                </BlockToolbar>
            );
        }
    }
);

const productGalleryStyles = {
    container: {
        margin: '12px 0',
        padding: '0'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
        gap: '12px'
    },
    titleInput: {
        border: 'none',
        fontSize: '18px',
        fontWeight: 700,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        flex: 1
    },
    styleToggle: {
        display: 'flex',
        gap: '4px'
    },
    toggleBtn: {
        padding: '4px 10px',
        border: '1px solid #e5e5e5',
        borderRadius: '4px',
        fontSize: '11px',
        cursor: 'pointer',
        backgroundColor: '#fff'
    },
    toggleActive: {
        backgroundColor: '#1a1a1a',
        color: '#fff',
        borderColor: '#1a1a1a'
    },
    scrollContainer: {
        overflowX: 'auto',
        overflowY: 'hidden',
        margin: '0 -8px',
        padding: '0 8px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
    },
    scrollRow: {
        display: 'flex',
        gap: '12px',
        paddingBottom: '8px'
    },
    gridContainer: {
        margin: '0'
    },
    gridLayout: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px'
    },
    productCard: {
        flex: '0 0 140px',
        width: '140px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        position: 'relative'
    },
    imageArea: {
        width: '100%',
        height: '120px',
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: '#f8f8f8'
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    imagePlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
    },
    productInfo: {
        padding: '8px 10px 4px'
    },
    productName: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#1a1a1a',
        marginBottom: '2px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    productPrice: {
        fontSize: '14px',
        fontWeight: 700,
        color: '#1a1a1a'
    },
    affiliateBadge: {
        display: 'inline-block',
        fontSize: '9px',
        fontWeight: 600,
        color: '#fff',
        backgroundColor: '#10b981',
        padding: '2px 6px',
        borderRadius: '4px',
        marginTop: '4px'
    },
    nameInput: {
        width: '100%',
        border: 'none',
        fontSize: '13px',
        fontWeight: 600,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        marginBottom: '2px',
        boxSizing: 'border-box'
    },
    priceInput: {
        width: '100%',
        border: 'none',
        fontSize: '14px',
        fontWeight: 700,
        color: '#1a1a1a',
        outline: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        boxSizing: 'border-box'
    },
    buttonWrapper: {
        padding: '6px 10px 10px'
    },
    actionButton: {
        display: 'block',
        width: 'calc(100% - 20px)',
        margin: '0 10px 10px',
        padding: '8px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 600,
        textAlign: 'center',
        boxSizing: 'border-box'
    },
    urlInput: {
        width: '100%',
        border: 'none',
        borderBottom: '1px dashed #ddd',
        fontSize: '10px',
        color: '#999',
        outline: 'none',
        backgroundColor: 'transparent',
        padding: '4px 0',
        marginTop: '4px',
        boxSizing: 'border-box'
    },
    cardActions: {
        position: 'absolute',
        top: '6px',
        right: '6px',
        display: 'flex',
        gap: '4px',
        zIndex: 2
    },
    actionBtn: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        border: '1px solid rgba(0,0,0,0.1)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    removeBtn: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        backgroundColor: '#fff',
        color: '#ff4444',
        border: '1px solid rgba(0,0,0,0.1)',
        fontSize: '16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    settingsPanel: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '320px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflowY: 'auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        padding: '20px',
        zIndex: 1001
    },
    settingsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #eee'
    },
    closeBtn: {
        width: '24px',
        height: '24px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    settingsField: {
        marginBottom: '12px'
    },
    fieldLabel: {
        display: 'block',
        fontSize: '12px',
        fontWeight: 500,
        color: '#666',
        marginBottom: '4px'
    },
    fieldInput: {
        width: '100%',
        padding: '8px 10px',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        fontSize: '13px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        caretColor: '#000'
    },
    doneBtn: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        marginTop: '8px'
    },
    addCard: {
        flex: '0 0 140px',
        width: '140px',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#fafafa',
        borderRadius: '12px',
        border: '2px dashed #ddd',
        cursor: 'pointer',
        color: '#999',
        fontSize: '12px'
    },
    // Browser Modal Styles
    browserModal: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '600px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    browserHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        borderBottom: '1px solid #e5e5e5'
    },
    browserBackBtn: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        border: '1px solid #e5e5e5',
        backgroundColor: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    browserSearch: {
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        caretColor: '#000'
    },
    browserContent: {
        flex: 1,
        overflow: 'auto',
        padding: '16px'
    },
    browserGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: '12px'
    },
    browserCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e5e5e5',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        position: 'relative'
    },
    browserCardImage: {
        width: '100%',
        aspectRatio: '1',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
    },
    browserCardInfo: {
        padding: '10px'
    },
    selectedBadge: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: '#3b82f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2
    },
    browserFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        borderTop: '1px solid #e5e5e5',
        backgroundColor: '#fafafa'
    },
    browserAddBtn: {
        padding: '10px 24px',
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer'
    },
    // Brand Badge
    brandBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        margin: '0 10px 8px',
        backgroundColor: '#eff6ff',
        color: '#3b82f6',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 500
    },
    // Cart Indicator
    cartIndicator: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 20px',
        marginTop: '12px',
        backgroundColor: '#3b82f6',
        color: '#fff',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        position: 'relative'
    },
    cartBadge: {
        position: 'absolute',
        top: '-6px',
        left: '28px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: '#ef4444',
        color: '#fff',
        fontSize: '11px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    // Checkout Modal
    checkoutModal: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '420px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    checkoutHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e5e5'
    },
    checkoutItems: {
        flex: 1,
        overflow: 'auto',
        padding: '12px 0',
        maxHeight: '300px'
    },
    checkoutItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 20px',
        borderBottom: '1px solid #f5f5f5'
    },
    checkoutItemImage: {
        width: '50px',
        height: '50px',
        borderRadius: '6px',
        overflow: 'hidden',
        flexShrink: 0
    },
    checkoutItemInfo: {
        flex: 1,
        minWidth: 0
    },
    checkoutItemActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '6px'
    },
    quantityControl: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    quantityBtn: {
        width: '24px',
        height: '24px',
        borderRadius: '4px',
        border: '1px solid #e5e5e5',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    checkoutFooter: {
        padding: '16px 20px',
        borderTop: '1px solid #e5e5e5',
        backgroundColor: '#fafafa'
    },
    checkoutBtn: {
        width: '100%',
        padding: '14px',
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        marginBottom: '8px'
    },
    continueBtn: {
        width: '100%',
        padding: '12px',
        backgroundColor: 'transparent',
        color: '#666',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer'
    }
};
